import cn from 'classnames'
import {AnimatePresence, motion} from 'framer-motion'
import type {AnimatedListSliceMapPrimaryElementsItem} from 'prismicio-types'
import {useWindowSize} from 'react-use'
import {BREAKPOINTS} from 'src/common/constants'

export const MapSvg = ({
  activeIndex,
  setActiveIndex,
  elements,
}: {
  activeIndex: number
  setActiveIndex: (index: number) => void
  elements: AnimatedListSliceMapPrimaryElementsItem[]
}) => {
  // Position mapping for each st1 element (relative to SVG viewBox)
  const elementPositions = [
    {x: 184.99, y: 196.68}, // st1-element-3 (activeIndex 0)
    {x: 95.46, y: 179.34}, // st1-element-1 (activeIndex 1)
    {x: 125.32, y: 185.12}, // st1-element-2 (activeIndex 2)
    {x: 220.81, y: 202.46}, // st1-element-0 (activeIndex 3)
    {x: 244.67, y: 127.27}, // st1-element-4 (activeIndex 4)
  ]

  const isMobile = useWindowSize().width < BREAKPOINTS.DESKTOP

  // Better text width calculation function
  const getTextWidth = (text: string) => {
    // Account for different character types and uppercase text
    // Average character width for 6px font-size, weight 600, uppercase
    const avgCharWidth = 4.7 // Increased for better fit
    const padding = 10 // Increased padding for better visual balance

    // Add extra width for wider characters (W, M, etc.)
    const wideCharCount = (text.match(/[WMQGD]/g) || []).length
    const narrowCharCount = (text.match(/[ILJ]/g) || []).length
    const extraWidth = wideCharCount * 1.8 - narrowCharCount * 0.8

    return Math.max(text.length * avgCharWidth + padding + extraWidth, text.length * 4.7 + 16)
  }

  return (
    <svg
      id="Layer_1"
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      viewBox="0 0 310.11323353293415 332.73"
      className="absolute right-[-11vw] h-[clamp(500px,20vw,540px)] max-md:top-[-12vw] min-[436px]:right-0 md:right-[-150px] md:top-0 md:h-[561px] md:w-[730px] lg:right-[-45px] lg:h-[648px]"
    >
      <rect id="rect-1" className="fill-[#426153]" x="5.96" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-2" className="fill-[#426153]" x="304.35" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-3" className="fill-[#426153]" x="405.81" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-4" className="fill-[#426153]" x="417.74" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-5" className="fill-[#426153]" x="405.81" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-6" className="fill-[#426153]" x="417.74" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-7" className="fill-[#426153]" x="429.66" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-8" className="fill-[#426153]" x="399.84" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-9" className="fill-[#426153]" x="411.77" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-10" className="fill-[#426153]" x="423.7" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-11" className="fill-[#426153]" x="399.84" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-12" className="fill-[#426153]" x="411.77" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-13" className="fill-[#426153]" x="399.84" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-14" className="fill-[#426153]" x="411.77" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-15" className="fill-[#426153]" x="399.84" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-16" className="fill-[#426153]" x="411.77" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-17" className="fill-[#426153]" x="405.81" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-18" className="fill-[#426153]" x="405.81" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-19" className="fill-[#426153]" x="405.81" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-20" className="fill-[#426153]" x="417.74" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-21" className="fill-[#426153]" x="417.74" y="277.76" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-22" className="fill-[#426153]" x="405.81" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-23" className="fill-[#426153]" x="411.77" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-24" className="fill-[#426153]" x="399.84" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-25" className="fill-[#426153]" x="399.84" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-26" className="fill-[#426153]" x="405.81" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-27" className="fill-[#426153]" x="340.17" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-28" className="fill-[#426153]" x="352.09" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-29" className="fill-[#426153]" x="364.02" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-30" className="fill-[#426153]" x="346.13" y="28.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-31" className="fill-[#426153]" x="358.06" y="28.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-32" className="fill-[#426153]" x="340.17" y="23.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-33" className="fill-[#426153]" x="352.09" y="23.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-34" className="fill-[#426153]" x="364.02" y="23.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-35" className="fill-[#426153]" x="352.09" y="17.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-36" className="fill-[#426153]" x="364.02" y="17.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-37" className="fill-[#426153]" x="358.06" y="11.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-38" className="fill-[#426153]" x="352.09" y="5.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-39" className="fill-[#426153]" x="352.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-40" className="fill-[#426153]" x="346.13" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-41" className="fill-[#426153]" x="358.06" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-42" className="fill-[#426153]" x="369.99" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-43" className="fill-[#426153]" x="381.92" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-44" className="fill-[#426153]" x="393.84" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-45" className="fill-[#426153]" x="346.13" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-46" className="fill-[#426153]" x="358.06" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-47" className="fill-[#426153]" x="369.99" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-48" className="fill-[#426153]" x="381.92" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-49" className="fill-[#426153]" x="340.17" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-50" className="fill-[#426153]" x="352.09" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-51" className="fill-[#426153]" x="364.02" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-52" className="fill-[#426153]" x="375.95" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-53" className="fill-[#426153]" x="387.88" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-54" className="fill-[#426153]" x="346.13" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-55" className="fill-[#426153]" x="358.06" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-56" className="fill-[#426153]" x="369.99" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-57" className="fill-[#426153]" x="381.92" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-58" className="fill-[#426153]" x="346.13" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-59" className="fill-[#426153]" x="358.06" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-60" className="fill-[#426153]" x="369.99" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-61" className="fill-[#426153]" x="381.92" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-62" className="fill-[#426153]" x="346.13" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-63" className="fill-[#426153]" x="358.06" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-64" className="fill-[#426153]" x="369.99" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-65" className="fill-[#426153]" x="340.17" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-66" className="fill-[#426153]" x="352.09" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-67" className="fill-[#426153]" x="364.02" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-68" className="fill-[#426153]" x="375.95" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-69" className="fill-[#426153]" x="346.13" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-70" className="fill-[#426153]" x="358.06" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-71" className="fill-[#426153]" x="369.99" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-72" className="fill-[#426153]" x="346.13" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-73" className="fill-[#426153]" x="358.06" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-74" className="fill-[#426153]" x="340.17" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-75" className="fill-[#426153]" x="352.09" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-76" className="fill-[#426153]" x="364.02" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-77" className="fill-[#426153]" x="346.13" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-78" className="fill-[#426153]" x="358.06" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-79" className="fill-[#426153]" x="369.99" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-80" className="fill-[#426153]" x="381.92" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-81" className="fill-[#426153]" x="393.84" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-82" className="fill-[#426153]" x="346.13" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-83" className="fill-[#426153]" x="358.06" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-84" className="fill-[#426153]" x="369.99" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-85" className="fill-[#426153]" x="381.92" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-86" className="fill-[#426153]" x="393.84" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-87" className="fill-[#426153]" x="346.13" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-88" className="fill-[#426153]" x="358.06" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-89" className="fill-[#426153]" x="369.99" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-90" className="fill-[#426153]" x="381.92" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-91" className="fill-[#426153]" x="393.84" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-92" className="fill-[#426153]" x="346.13" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-93" className="fill-[#426153]" x="358.06" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-94" className="fill-[#426153]" x="369.99" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-95" className="fill-[#426153]" x="381.92" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-96" className="fill-[#426153]" x="393.84" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-97" className="fill-[#426153]" x="346.13" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-98" className="fill-[#426153]" x="358.06" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-99" className="fill-[#426153]" x="369.99" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-100" className="fill-[#426153]" x="381.92" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-101" className="fill-[#426153]" x="393.84" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-102" className="fill-[#426153]" x="346.13" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-103" className="fill-[#426153]" x="358.06" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-104" className="fill-[#426153]" x="369.99" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-105" className="fill-[#426153]" x="381.92" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-106" className="fill-[#426153]" x="393.84" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-107" className="fill-[#426153]" x="346.13" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-108" className="fill-[#426153]" x="358.06" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-109" className="fill-[#426153]" x="369.99" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-110" className="fill-[#426153]" x="381.92" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-111" className="fill-[#426153]" x="393.84" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-112" className="fill-[#426153]" x="346.13" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-113" className="fill-[#426153]" x="358.06" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-114" className="fill-[#426153]" x="369.99" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-115" className="fill-[#426153]" x="381.92" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-116" className="fill-[#426153]" x="393.84" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-117" className="fill-[#426153]" x="346.13" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-118" className="fill-[#426153]" x="358.06" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-119" className="fill-[#426153]" x="369.99" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-120" className="fill-[#426153]" x="381.92" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-121" className="fill-[#426153]" x="393.84" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-122" className="fill-[#426153]" x="346.13" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-123" className="fill-[#426153]" x="358.06" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-124" className="fill-[#426153]" x="369.99" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-125" className="fill-[#426153]" x="381.92" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-126" className="fill-[#426153]" x="340.17" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-127" className="fill-[#426153]" x="352.09" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-128" className="fill-[#426153]" x="364.02" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-129" className="fill-[#426153]" x="375.95" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-130" className="fill-[#426153]" x="387.88" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-131" className="fill-[#426153]" x="346.13" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-132" className="fill-[#426153]" x="358.06" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-133" className="fill-[#426153]" x="369.99" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-134" className="fill-[#426153]" x="340.17" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-135" className="fill-[#426153]" x="352.09" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-136" className="fill-[#426153]" x="364.02" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-137" className="fill-[#426153]" x="340.17" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-138" className="fill-[#426153]" x="352.09" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-139" className="fill-[#426153]" x="364.02" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-140" className="fill-[#426153]" x="340.17" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-141" className="fill-[#426153]" x="352.09" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-142" className="fill-[#426153]" x="364.02" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-143" className="fill-[#426153]" x="340.17" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-144" className="fill-[#426153]" x="352.09" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-145" className="fill-[#426153]" x="364.02" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-146" className="fill-[#426153]" x="375.95" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-147" className="fill-[#426153]" x="381.87" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-148" className="fill-[#426153]" x="346.13" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-149" className="fill-[#426153]" x="358.06" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-150" className="fill-[#426153]" x="369.99" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-151" className="fill-[#426153]" x="340.17" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-152" className="fill-[#426153]" x="352.09" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-153" className="fill-[#426153]" x="364.02" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-154" className="fill-[#426153]" x="375.95" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-155" className="fill-[#426153]" x="340.17" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-156" className="fill-[#426153]" x="352.09" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-157" className="fill-[#426153]" x="364.02" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-158" className="fill-[#426153]" x="375.95" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-159" className="fill-[#426153]" x="340.17" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-160" className="fill-[#426153]" x="352.09" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-161" className="fill-[#426153]" x="364.02" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-162" className="fill-[#426153]" x="375.95" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-163" className="fill-[#426153]" x="387.88" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-164" className="fill-[#426153]" x="375.95" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-165" className="fill-[#426153]" x="387.88" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-166" className="fill-[#426153]" x="364.02" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-167" className="fill-[#426153]" x="375.95" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-168" className="fill-[#426153]" x="387.88" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-169" className="fill-[#426153]" x="358.06" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-170" className="fill-[#426153]" x="369.99" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-171" className="fill-[#426153]" x="381.92" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-172" className="fill-[#426153]" x="393.84" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-173" className="fill-[#426153]" x="352.09" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-174" className="fill-[#426153]" x="364.02" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-175" className="fill-[#426153]" x="375.95" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-176" className="fill-[#426153]" x="387.88" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-177" className="fill-[#426153]" x="340.17" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-178" className="fill-[#426153]" x="346.13" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-179" className="fill-[#426153]" x="358.06" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-180" className="fill-[#426153]" x="369.99" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-181" className="fill-[#426153]" x="381.92" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-182" className="fill-[#426153]" x="393.84" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-183" className="fill-[#426153]" x="346.13" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-184" className="fill-[#426153]" x="358.06" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-185" className="fill-[#426153]" x="369.99" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-186" className="fill-[#426153]" x="381.92" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-187" className="fill-[#426153]" x="340.17" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-188" className="fill-[#426153]" x="352.09" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-189" className="fill-[#426153]" x="364.02" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-190" className="fill-[#426153]" x="375.95" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-191" className="fill-[#426153]" x="340.17" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-192" className="fill-[#426153]" x="352.09" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-193" className="fill-[#426153]" x="364.02" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-194" className="fill-[#426153]" x="375.95" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-195" className="fill-[#426153]" x="340.17" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-196" className="fill-[#426153]" x="352.09" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-197" className="fill-[#426153]" x="364.02" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-198" className="fill-[#426153]" x="375.95" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-199" className="fill-[#426153]" x="340.17" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-200" className="fill-[#426153]" x="352.09" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-201" className="fill-[#426153]" x="364.02" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-202" className="fill-[#426153]" x="375.95" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-203" className="fill-[#426153]" x="387.88" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-204" className="fill-[#426153]" x="381.92" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-205" className="fill-[#426153]" x="393.84" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-206" className="fill-[#426153]" x="328.2" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-207" className="fill-[#426153]" x="328.2" y="28.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-208" className="fill-[#426153]" x="280.49" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-209" className="fill-[#426153]" x="292.42" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-210" className="fill-[#426153]" x="304.35" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-211" className="fill-[#426153]" x="316.27" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-212" className="fill-[#426153]" x="328.2" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-213" className="fill-[#426153]" x="280.49" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-214" className="fill-[#426153]" x="292.42" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-215" className="fill-[#426153]" x="304.35" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-216" className="fill-[#426153]" x="316.27" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-217" className="fill-[#426153]" x="328.2" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-218" className="fill-[#426153]" x="280.49" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-219" className="fill-[#426153]" x="292.42" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-220" className="fill-[#426153]" x="304.35" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-221" className="fill-[#426153]" x="316.27" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-222" className="fill-[#426153]" x="328.2" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-223" className="fill-[#426153]" x="280.49" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-224" className="fill-[#426153]" x="292.42" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-225" className="fill-[#426153]" x="304.35" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-226" className="fill-[#426153]" x="316.27" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-227" className="fill-[#426153]" x="328.2" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-228" className="fill-[#426153]" x="280.49" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-229" className="fill-[#426153]" x="292.42" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-230" className="fill-[#426153]" x="304.35" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-231" className="fill-[#426153]" x="316.27" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-232" className="fill-[#426153]" x="328.2" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-233" className="fill-[#426153]" x="280.49" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-234" className="fill-[#426153]" x="292.42" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-235" className="fill-[#426153]" x="304.35" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-236" className="fill-[#426153]" x="316.27" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-237" className="fill-[#426153]" x="328.2" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-238" className="fill-[#426153]" x="280.49" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-239" className="fill-[#426153]" x="292.42" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-240" className="fill-[#426153]" x="304.35" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-241" className="fill-[#426153]" x="316.27" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-242" className="fill-[#426153]" x="328.2" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-243" className="fill-[#426153]" x="280.49" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-244" className="fill-[#426153]" x="292.42" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-245" className="fill-[#426153]" x="304.35" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-246" className="fill-[#426153]" x="316.27" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-247" className="fill-[#426153]" x="328.2" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-248" className="fill-[#426153]" x="280.49" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-249" className="fill-[#426153]" x="292.42" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-250" className="fill-[#426153]" x="304.35" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-251" className="fill-[#426153]" x="316.27" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-252" className="fill-[#426153]" x="328.2" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-253" className="fill-[#426153]" x="280.49" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-254" className="fill-[#426153]" x="292.42" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-255" className="fill-[#426153]" x="304.35" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-256" className="fill-[#426153]" x="316.27" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-257" className="fill-[#426153]" x="328.2" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-258" className="fill-[#426153]" x="280.49" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-259" className="fill-[#426153]" x="292.42" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-260" className="fill-[#426153]" x="304.35" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-261" className="fill-[#426153]" x="316.27" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-262" className="fill-[#426153]" x="328.2" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-263" className="fill-[#426153]" x="280.49" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-264" className="fill-[#426153]" x="292.42" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-265" className="fill-[#426153]" x="304.35" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-266" className="fill-[#426153]" x="316.27" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-267" className="fill-[#426153]" x="328.2" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-268" className="fill-[#426153]" x="280.49" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-269" className="fill-[#426153]" x="292.42" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-270" className="fill-[#426153]" x="304.35" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-271" className="fill-[#426153]" x="316.27" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-272" className="fill-[#426153]" x="328.2" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-273" className="fill-[#426153]" x="280.49" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-274" className="fill-[#426153]" x="292.42" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-275" className="fill-[#426153]" x="304.35" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-276" className="fill-[#426153]" x="316.27" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-277" className="fill-[#426153]" x="328.2" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-278" className="fill-[#426153]" x="280.49" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-279" className="fill-[#426153]" x="292.42" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-280" className="fill-[#426153]" x="304.35" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-281" className="fill-[#426153]" x="316.27" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-282" className="fill-[#426153]" x="328.2" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-283" className="fill-[#426153]" x="280.49" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-284" className="fill-[#426153]" x="292.42" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-285" className="fill-[#426153]" x="304.35" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-286" className="fill-[#426153]" x="316.27" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-287" className="fill-[#426153]" x="328.2" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-288" className="fill-[#426153]" x="280.49" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-289" className="fill-[#426153]" x="292.42" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-290" className="fill-[#426153]" x="304.35" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-291" className="fill-[#426153]" x="316.27" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-292" className="fill-[#426153]" x="328.2" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-293" className="fill-[#426153]" x="280.49" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-294" className="fill-[#426153]" x="292.42" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-295" className="fill-[#426153]" x="304.35" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-296" className="fill-[#426153]" x="316.27" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-297" className="fill-[#426153]" x="328.2" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-298" className="fill-[#426153]" x="280.49" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-299" className="fill-[#426153]" x="292.42" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-300" className="fill-[#426153]" x="304.35" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-301" className="fill-[#426153]" x="316.27" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-302" className="fill-[#426153]" x="328.2" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-303" className="fill-[#426153]" x="280.49" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-304" className="fill-[#426153]" x="292.42" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-305" className="fill-[#426153]" x="304.35" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-306" className="fill-[#426153]" x="316.27" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-307" className="fill-[#426153]" x="328.2" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-308" className="fill-[#426153]" x="334.17" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-309" className="fill-[#426153]" x="280.49" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-310" className="fill-[#426153]" x="292.42" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-311" className="fill-[#426153]" x="304.35" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-312" className="fill-[#426153]" x="334.17" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-313" className="fill-[#426153]" x="286.45" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-314" className="fill-[#426153]" x="298.38" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-315" className="fill-[#426153]" x="310.31" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-316" className="fill-[#426153]" x="280.49" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-317" className="fill-[#426153]" x="292.42" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-318" className="fill-[#426153]" x="304.35" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-319" className="fill-[#426153]" x="316.27" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-320" className="fill-[#426153]" x="328.2" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-321" className="fill-[#426153]" x="280.49" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-322" className="fill-[#426153]" x="292.42" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-323" className="fill-[#426153]" x="304.35" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-324" className="fill-[#426153]" x="316.27" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-325" className="fill-[#426153]" x="328.2" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-326" className="fill-[#426153]" x="226.78" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-327" className="fill-[#426153]" x="238.71" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-328" className="fill-[#426153]" x="250.63" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-329" className="fill-[#426153]" x="262.56" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-330" className="fill-[#426153]" x="274.49" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-331" className="fill-[#426153]" x="238.71" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-332" className="fill-[#426153]" x="250.63" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-333" className="fill-[#426153]" x="262.56" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-334" className="fill-[#426153]" x="274.49" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-335" className="fill-[#426153]" x="238.71" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-336" className="fill-[#426153]" x="250.63" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-337" className="fill-[#426153]" x="262.56" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-338" className="fill-[#426153]" x="274.49" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-339" className="fill-[#426153]" x="244.67" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-340" className="fill-[#426153]" x="256.6" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-341" className="fill-[#426153]" x="268.53" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-342" className="fill-[#426153]" x="244.67" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-343" className="fill-[#426153]" x="256.6" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-344" className="fill-[#426153]" x="268.53" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-345" className="fill-[#426153]" x="226.78" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-346" className="fill-[#426153]" x="238.71" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-347" className="fill-[#426153]" x="250.63" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-348" className="fill-[#426153]" x="262.56" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-349" className="fill-[#426153]" x="226.78" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-350" className="fill-[#426153]" x="238.71" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-351" className="fill-[#426153]" x="250.63" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-352" className="fill-[#426153]" x="220.81" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-353" className="fill-[#426153]" x="232.74" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-354" className="fill-[#426153]" x="244.67" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-355" className="fill-[#426153]" x="256.6" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-356" className="fill-[#426153]" x="268.53" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-357" className="fill-[#426153]" x="220.81" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-358" className="fill-[#426153]" x="232.74" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-359" className="fill-[#426153]" x="244.67" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-360" className="fill-[#426153]" x="256.6" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-361" className="fill-[#426153]" x="268.53" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-362" className="fill-[#426153]" x="220.81" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-363" className="fill-[#426153]" x="232.74" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-364" className="fill-[#426153]" x="244.67" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-365" className="fill-[#426153]" x="256.6" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-366" className="fill-[#426153]" x="268.53" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-367" className="fill-[#426153]" x="226.78" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-368" className="fill-[#426153]" x="232.74" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-369" className="fill-[#426153]" x="244.67" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-370" className="fill-[#426153]" x="256.6" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-371" className="fill-[#426153]" x="268.53" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-372" className="fill-[#426153]" x="238.71" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-373" className="fill-[#426153]" x="250.63" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-374" className="fill-[#426153]" x="262.56" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-375" className="fill-[#426153]" x="274.49" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-376" className="fill-[#426153]" x="244.67" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-377" className="fill-[#426153]" x="256.6" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-378" className="fill-[#426153]" x="268.53" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-379"
        className="fill-[#426153]"
        x={activeIndex === 4 ? '237.71' : '238.71'}
        y={activeIndex === 4 ? '134.05' : '133.05'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-380"
        className="fill-[#426153]"
        x={activeIndex === 4 ? '251.63' : '250.63'}
        y={activeIndex === 4 ? '134.05' : '133.05'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-381" className="fill-[#426153]" x="262.56" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-382" className="fill-[#426153]" x="274.49" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-383" className="fill-[#426153]" x="262.56" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-384" className="fill-[#426153]" x="274.49" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-385" className="fill-[#426153]" x="274.49" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-386" className="fill-[#426153]" x="232.74" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-387" className="fill-[#426153]" x="268.53" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-388" className="fill-[#426153]" x="226.78" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-389" className="fill-[#426153]" x="238.71" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-390" className="fill-[#426153]" x="250.63" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-391" className="fill-[#426153]" x="262.56" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-392" className="fill-[#426153]" x="274.49" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-393" className="fill-[#426153]" x="232.74" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-394" className="fill-[#426153]" x="244.67" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-395" className="fill-[#426153]" x="256.6" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-396" className="fill-[#426153]" x="268.53" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-397" className="fill-[#426153]" x="220.81" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-398" className="fill-[#426153]" x="232.74" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-399" className="fill-[#426153]" x="244.67" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-400" className="fill-[#426153]" x="256.6" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-401" className="fill-[#426153]" x="268.53" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-402"
        className="fill-[#426153]"
        x="220.81"
        y={activeIndex === 3 ? '209.24' : '208.24'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-403" className="fill-[#426153]" x="232.74" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-404" className="fill-[#426153]" x="244.67" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-405" className="fill-[#426153]" x="256.6" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-406" className="fill-[#426153]" x="268.53" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-407" className="fill-[#426153]" x="232.74" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-408" className="fill-[#426153]" x="244.67" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-409" className="fill-[#426153]" x="256.6" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-410" className="fill-[#426153]" x="268.53" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-411"
        className="fill-[#426153]"
        x="220.81"
        y={activeIndex === 3 ? '195.68' : '196.68'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-412" className="fill-[#426153]" x="232.74" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-413" className="fill-[#426153]" x="244.67" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-414" className="fill-[#426153]" x="256.6" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-415" className="fill-[#426153]" x="268.53" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-416" className="fill-[#426153]" x="220.81" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-417" className="fill-[#426153]" x="232.74" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-418" className="fill-[#426153]" x="244.67" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-419" className="fill-[#426153]" x="256.6" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-420" className="fill-[#426153]" x="268.53" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-421" className="fill-[#426153]" x="220.81" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-422" className="fill-[#426153]" x="232.74" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-423" className="fill-[#426153]" x="244.67" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-424" className="fill-[#426153]" x="256.6" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-425" className="fill-[#426153]" x="268.53" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-426" className="fill-[#426153]" x="220.81" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-427" className="fill-[#426153]" x="232.74" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-428" className="fill-[#426153]" x="244.67" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-429" className="fill-[#426153]" x="256.6" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-430" className="fill-[#426153]" x="268.53" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-431" className="fill-[#426153]" x="220.81" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-432" className="fill-[#426153]" x="232.74" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-433" className="fill-[#426153]" x="244.67" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-434" className="fill-[#426153]" x="256.6" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-435" className="fill-[#426153]" x="268.53" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-436" className="fill-[#426153]" x="226.78" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-437" className="fill-[#426153]" x="238.71" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-438" className="fill-[#426153]" x="250.63" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-439" className="fill-[#426153]" x="262.56" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-440" className="fill-[#426153]" x="274.49" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-441" className="fill-[#426153]" x="232.74" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-442" className="fill-[#426153]" x="244.67" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-443" className="fill-[#426153]" x="256.6" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-444" className="fill-[#426153]" x="268.53" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-445" className="fill-[#426153]" x="220.81" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-446" className="fill-[#426153]" x="226.78" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-447" className="fill-[#426153]" x="238.71" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-448" className="fill-[#426153]" x="250.63" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-449" className="fill-[#426153]" x="262.56" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-450" className="fill-[#426153]" x="274.49" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-451" className="fill-[#426153]" x="226.78" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-452" className="fill-[#426153]" x="238.71" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-453" className="fill-[#426153]" x="250.63" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-454" className="fill-[#426153]" x="262.56" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-455" className="fill-[#426153]" x="220.81" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-456" className="fill-[#426153]" x="232.74" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-457" className="fill-[#426153]" x="244.67" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-458" className="fill-[#426153]" x="256.6" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-459" className="fill-[#426153]" x="220.81" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-460" className="fill-[#426153]" x="232.74" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-461" className="fill-[#426153]" x="244.67" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-462" className="fill-[#426153]" x="256.6" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-463" className="fill-[#426153]" x="268.53" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-464" className="fill-[#426153]" x="226.78" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-465" className="fill-[#426153]" x="238.71" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-466" className="fill-[#426153]" x="250.63" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-467" className="fill-[#426153]" x="262.56" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-468" className="fill-[#426153]" x="220.81" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-469" className="fill-[#426153]" x="232.74" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-470" className="fill-[#426153]" x="244.67" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-471" className="fill-[#426153]" x="256.6" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-472" className="fill-[#426153]" x="268.53" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-473" className="fill-[#426153]" x="220.81" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-474" className="fill-[#426153]" x="232.74" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-475" className="fill-[#426153]" x="244.67" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-476" className="fill-[#426153]" x="256.6" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-477" className="fill-[#426153]" x="268.53" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-478" className="fill-[#426153]" x="220.81" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-479" className="fill-[#426153]" x="232.74" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-480" className="fill-[#426153]" x="244.67" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-481" className="fill-[#426153]" x="256.6" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-482" className="fill-[#426153]" x="268.53" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-483" className="fill-[#426153]" x="220.81" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-484" className="fill-[#426153]" x="232.74" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-485" className="fill-[#426153]" x="244.67" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-486" className="fill-[#426153]" x="256.6" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-487" className="fill-[#426153]" x="268.53" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-488" className="fill-[#426153]" x="220.81" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-489" className="fill-[#426153]" x="232.74" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-490" className="fill-[#426153]" x="244.67" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-491" className="fill-[#426153]" x="256.6" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-492" className="fill-[#426153]" x="268.53" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-493" className="fill-[#426153]" x="220.81" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-494" className="fill-[#426153]" x="232.74" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-495" className="fill-[#426153]" x="244.67" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-496" className="fill-[#426153]" x="256.6" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-497" className="fill-[#426153]" x="268.53" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-498" className="fill-[#426153]" x="167.1" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-499" className="fill-[#426153]" x="179.03" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-500" className="fill-[#426153]" x="190.96" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-501" className="fill-[#426153]" x="202.89" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-502" className="fill-[#426153]" x="179.03" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-503" className="fill-[#426153]" x="190.96" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-504" className="fill-[#426153]" x="202.89" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-505" className="fill-[#426153]" x="184.99" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-506" className="fill-[#426153]" x="196.92" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-507" className="fill-[#426153]" x="208.85" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-508" className="fill-[#426153]" x="184.99" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-509" className="fill-[#426153]" x="196.92" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-510" className="fill-[#426153]" x="208.85" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-511" className="fill-[#426153]" x="190.96" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-512" className="fill-[#426153]" x="202.89" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-513" className="fill-[#426153]" x="214.81" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-514" className="fill-[#426153]" x="196.92" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-515" className="fill-[#426153]" x="208.85" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-516" className="fill-[#426153]" x="196.92" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-517" className="fill-[#426153]" x="208.85" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-518" className="fill-[#426153]" x="202.89" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-519" className="fill-[#426153]" x="214.81" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-520" className="fill-[#426153]" x="214.81" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-521" className="fill-[#426153]" x="179.03" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-522" className="fill-[#426153]" x="190.96" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-523" className="fill-[#426153]" x="184.99" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-524" className="fill-[#426153]" x="179.03" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-525" className="fill-[#426153]" x="190.96" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-526" className="fill-[#426153]" x="179.03" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-527" className="fill-[#426153]" x="190.96" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-528" className="fill-[#426153]" x="184.99" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-529" className="fill-[#426153]" x="196.92" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-530" className="fill-[#426153]" x="173.07" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-531" className="fill-[#426153]" x="184.99" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-532" className="fill-[#426153]" x="196.92" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-533" className="fill-[#426153]" x="167.1" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-534" className="fill-[#426153]" x="179.03" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-535" className="fill-[#426153]" x="190.96" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-536" className="fill-[#426153]" x="161.14" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-537" className="fill-[#426153]" x="173.07" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-538" className="fill-[#426153]" x="184.99" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-539" className="fill-[#426153]" x="196.92" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-540" className="fill-[#426153]" x="167.1" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-541" className="fill-[#426153]" x="179.03" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-542" className="fill-[#426153]" x="190.96" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-543" className="fill-[#426153]" x="161.14" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-544" className="fill-[#426153]" x="173.07" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-545" className="fill-[#426153]" x="184.99" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-546" className="fill-[#426153]" x="196.92" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-547" className="fill-[#426153]" x="167.1" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-548" className="fill-[#426153]" x="179.03" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-549" className="fill-[#426153]" x="190.96" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-550" className="fill-[#426153]" x="202.89" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-551"
        className="fill-[#426153]"
        x={activeIndex === 3 ? '213.81' : '214.81'}
        y={activeIndex === 3 ? '209.24' : '208.24'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-552" className="fill-[#426153]" x="167.1" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-553"
        className="fill-[#426153]"
        x={activeIndex === 0 ? '178.03' : '179.03'}
        y={activeIndex === 0 ? '203.46' : '202.46'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-554"
        className="fill-[#426153]"
        x={activeIndex === 0 ? '191.96' : '190.96'}
        y={activeIndex === 0 ? '203.46' : '202.46'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-555" className="fill-[#426153]" x="202.89" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-556"
        className="fill-[#426153]"
        x={activeIndex === 3 ? '213.81' : '214.81'}
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-557" className="fill-[#426153]" x="167.1" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        className="fill-[#426153]"
        x={activeIndex === 0 ? '178.03' : '179.03'}
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        className="fill-[#426153]"
        x={activeIndex === 0 ? '191.96' : '190.96'}
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-558" className="fill-[#426153]" x="202.89" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-559"
        className="fill-[#426153]"
        x={activeIndex === 3 ? '213.81' : '214.81'}
        y={activeIndex === 3 ? '195.68' : '196.68'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-560" className="fill-[#426153]" x="167.1" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-561"
        className="fill-[#426153]"
        x={activeIndex === 0 ? '178.03' : '179.03'}
        y={activeIndex === 0 ? '189.9' : '190.9'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-562"
        className="fill-[#426153]"
        x={activeIndex === 0 ? '191.96' : '190.96'}
        y={activeIndex === 0 ? '189.9' : '190.9'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-563" className="fill-[#426153]" x="202.89" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-564" className="fill-[#426153]" x="214.81" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-565" className="fill-[#426153]" x="167.1" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-566" className="fill-[#426153]" x="179.03" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-567" className="fill-[#426153]" x="190.96" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-568" className="fill-[#426153]" x="202.89" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-569" className="fill-[#426153]" x="214.81" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-570" className="fill-[#426153]" x="167.1" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect className="fill-[#426153]" x="184.99" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-571" className="fill-[#426153]" x="196.92" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-572" className="fill-[#426153]" x="208.85" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-573" className="fill-[#426153]" x="196.92" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-574" className="fill-[#426153]" x="179.03" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-575" className="fill-[#426153]" x="190.96" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-576" className="fill-[#426153]" x="202.89" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-577" className="fill-[#426153]" x="214.81" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-578" className="fill-[#426153]" x="190.96" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-579" className="fill-[#426153]" x="202.89" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-580" className="fill-[#426153]" x="214.81" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-581" className="fill-[#426153]" x="184.99" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-582" className="fill-[#426153]" x="196.92" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-583" className="fill-[#426153]" x="208.85" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-584" className="fill-[#426153]" x="161.14" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-585" className="fill-[#426153]" x="173.07" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-586" className="fill-[#426153]" x="184.99" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-587" className="fill-[#426153]" x="196.92" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-588" className="fill-[#426153]" x="208.85" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-589" className="fill-[#426153]" x="161.14" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-590" className="fill-[#426153]" x="173.07" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-591" className="fill-[#426153]" x="184.99" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-592" className="fill-[#426153]" x="196.92" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-593" className="fill-[#426153]" x="208.85" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-594" className="fill-[#426153]" x="161.14" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-595" className="fill-[#426153]" x="173.07" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-596" className="fill-[#426153]" x="184.99" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-597" className="fill-[#426153]" x="196.92" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-598" className="fill-[#426153]" x="208.85" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-599" className="fill-[#426153]" x="161.14" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-600" className="fill-[#426153]" x="173.07" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-601" className="fill-[#426153]" x="184.99" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-602" className="fill-[#426153]" x="196.92" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-603" className="fill-[#426153]" x="208.85" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-604" className="fill-[#426153]" x="161.14" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-605" className="fill-[#426153]" x="173.07" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-606" className="fill-[#426153]" x="184.99" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-607" className="fill-[#426153]" x="196.92" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-608" className="fill-[#426153]" x="208.85" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-609" className="fill-[#426153]" x="161.14" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-610" className="fill-[#426153]" x="173.07" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-611" className="fill-[#426153]" x="184.99" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-612" className="fill-[#426153]" x="196.92" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-613" className="fill-[#426153]" x="208.85" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-614" className="fill-[#426153]" x="161.14" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-615" className="fill-[#426153]" x="173.07" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect className="fill-[#426153]" x="184.99" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-616" className="fill-[#426153]" x="196.92" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-617" className="fill-[#426153]" x="208.85" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-618" className="fill-[#426153]" x="184.99" y="300.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-619" className="fill-[#426153]" x="179.03" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-620" className="fill-[#426153]" x="190.96" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-621" className="fill-[#426153]" x="214.81" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-622" className="fill-[#426153]" x="179.03" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-623" className="fill-[#426153]" x="161.14" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-624" className="fill-[#426153]" x="208.85" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-625" className="fill-[#426153]" x="161.14" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-626" className="fill-[#426153]" x="202.89" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-627" className="fill-[#426153]" x="214.81" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-628" className="fill-[#426153]" x="196.92" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-629" className="fill-[#426153]" x="208.85" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-630" className="fill-[#426153]" x="149.17" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-631" className="fill-[#426153]" x="155.14" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-632" className="fill-[#426153]" x="155.14" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-633" className="fill-[#426153]" x="149.17" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-634" className="fill-[#426153]" x="149.17" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-635" className="fill-[#426153]" x="155.14" y="104.15" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-636" className="fill-[#426153]" x="149.17" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-637" className="fill-[#426153]" x="65.64" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-638" className="fill-[#426153]" x="83.53" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-639" className="fill-[#426153]" x="95.46" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-640" className="fill-[#426153]" x="47.75" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-641" className="fill-[#426153]" x="59.68" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-642" className="fill-[#426153]" x="71.6" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-643" className="fill-[#426153]" x="83.53" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-644" className="fill-[#426153]" x="41.79" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-645" className="fill-[#426153]" x="53.71" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-646" className="fill-[#426153]" x="65.64" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-647" className="fill-[#426153]" x="77.57" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-648" className="fill-[#426153]" x="89.5" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-649" className="fill-[#426153]" x="41.79" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-650" className="fill-[#426153]" x="53.71" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-651" className="fill-[#426153]" x="65.64" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-652" className="fill-[#426153]" x="77.57" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-653" className="fill-[#426153]" x="89.5" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-654" className="fill-[#426153]" x="41.79" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-655" className="fill-[#426153]" x="77.57" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-656" className="fill-[#426153]" x="89.5" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-657" className="fill-[#426153]" x="77.57" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-658" className="fill-[#426153]" x="89.5" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-659" className="fill-[#426153]" x="77.63" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-660" className="fill-[#426153]" x="89.5" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-661" className="fill-[#426153]" x="77.57" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-662" className="fill-[#426153]" x="89.5" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-663" className="fill-[#426153]" x="77.57" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-664" className="fill-[#426153]" x="89.5" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-665" className="fill-[#426153]" x="77.57" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-666" className="fill-[#426153]" x="89.5" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-667" className="fill-[#426153]" x="77.57" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-668" className="fill-[#426153]" x="89.5" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-669" className="fill-[#426153]" x="41.79" y="300.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-670" className="fill-[#426153]" x="53.71" y="300.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-671" className="fill-[#426153]" x="47.75" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-672" className="fill-[#426153]" x="59.68" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-673" className="fill-[#426153]" x="47.75" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-674" className="fill-[#426153]" x="59.68" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-675" className="fill-[#426153]" x="47.75" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-676" className="fill-[#426153]" x="59.68" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-677" className="fill-[#426153]" x="41.79" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-678" className="fill-[#426153]" x="53.71" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-679" className="fill-[#426153]" x="65.64" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-680" className="fill-[#426153]" x="77.57" y="300.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-681" className="fill-[#426153]" x="47.75" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-682" className="fill-[#426153]" x="59.68" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-683" className="fill-[#426153]" x="71.6" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-684" className="fill-[#426153]" x="83.53" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-685" className="fill-[#426153]" x="11.93" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-686" className="fill-[#426153]" x="23.86" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-687" className="fill-[#426153]" x="35.78" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-688" className="fill-[#426153]" x="17.89" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-689" className="fill-[#426153]" x="29.82" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-690" className="fill-[#426153]" x="17.89" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-691" className="fill-[#426153]" x="29.82" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-692" className="fill-[#426153]" x="17.89" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-693" className="fill-[#426153]" x="29.82" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-694" className="fill-[#426153]" x="23.86" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-695" className="fill-[#426153]" x="17.89" y="300.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-696" className="fill-[#426153]" x="29.82" y="300.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-697" className="fill-[#426153]" x="17.89" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-698" className="fill-[#426153]" x="29.82" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-699" className="fill-[#426153]" x="5.96" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-700" className="fill-[#426153]" x="17.89" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-701" className="fill-[#426153]" x="29.82" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-702" className="fill-[#426153]" x="5.96" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-703" className="fill-[#426153]" x="17.89" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-704" className="fill-[#426153]" x="29.82" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-705" className="fill-[#426153]" x="5.96" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-706" className="fill-[#426153]" x="17.89" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-707" className="fill-[#426153]" x="29.82" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-708" className="fill-[#426153]" x="5.96" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-709" className="fill-[#426153]" x="17.89" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-710" className="fill-[#426153]" x="29.82" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-711" className="fill-[#426153]" x="149.17" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-712" className="fill-[#426153]" x="113.39" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-713" className="fill-[#426153]" x="125.32" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-714" className="fill-[#426153]" x="155.14" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-715" className="fill-[#426153]" x="107.42" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-716" className="fill-[#426153]" x="119.35" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-717" className="fill-[#426153]" x="131.28" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-718" className="fill-[#426153]" x="143.21" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-719" className="fill-[#426153]" x="155.14" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-720" className="fill-[#426153]" x="107.42" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-721" className="fill-[#426153]" x="119.35" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-722" className="fill-[#426153]" x="131.28" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-723" className="fill-[#426153]" x="143.21" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-724" className="fill-[#426153]" x="155.14" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-725" className="fill-[#426153]" x="107.42" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-726" className="fill-[#426153]" x="119.35" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-727" className="fill-[#426153]" x="125.32" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-728" className="fill-[#426153]" x="143.21" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-729" className="fill-[#426153]" x="155.14" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-730" className="fill-[#426153]" x="107.42" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-731" className="fill-[#426153]" x="119.35" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-732" className="fill-[#426153]" x="131.28" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-733" className="fill-[#426153]" x="143.21" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-734" className="fill-[#426153]" x="155.14" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-735" className="fill-[#426153]" x="107.42" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-736" className="fill-[#426153]" x="119.35" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-737" className="fill-[#426153]" x="131.28" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-738" className="fill-[#426153]" x="143.21" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-739" className="fill-[#426153]" x="155.14" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-740" className="fill-[#426153]" x="107.42" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-741" className="fill-[#426153]" x="119.35" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-742" className="fill-[#426153]" x="131.28" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-743" className="fill-[#426153]" x="143.21" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-744" className="fill-[#426153]" x="155.14" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-745" className="fill-[#426153]" x="107.42" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-746" className="fill-[#426153]" x="119.35" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-747" className="fill-[#426153]" x="131.28" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-748" className="fill-[#426153]" x="143.21" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-749" className="fill-[#426153]" x="155.14" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-750" className="fill-[#426153]" x="107.42" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-751" className="fill-[#426153]" x="119.35" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-752" className="fill-[#426153]" x="131.28" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-753" className="fill-[#426153]" x="143.21" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-754" className="fill-[#426153]" x="155.14" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-755" className="fill-[#426153]" x="220.81" y="306.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-756" className="fill-[#426153]" x="220.81" y="300.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-757" className="fill-[#426153]" x="220.81" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-758" className="fill-[#426153]" x="220.81" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-759" className="fill-[#426153]" x="232.74" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-760" className="fill-[#426153]" x="244.67" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-761" className="fill-[#426153]" x="256.6" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-762" className="fill-[#426153]" x="220.81" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-763" className="fill-[#426153]" x="232.74" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-764" className="fill-[#426153]" x="244.67" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-765" className="fill-[#426153]" x="256.6" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-766" className="fill-[#426153]" x="220.81" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-767" className="fill-[#426153]" x="232.74" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-768" className="fill-[#426153]" x="244.67" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-769" className="fill-[#426153]" x="256.6" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-770" className="fill-[#426153]" x="226.78" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-771" className="fill-[#426153]" x="238.71" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-772" className="fill-[#426153]" x="250.63" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-773" className="fill-[#426153]" x="262.56" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-774" className="fill-[#426153]" x="280.49" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-775" className="fill-[#426153]" x="292.42" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-776" className="fill-[#426153]" x="304.35" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-777" className="fill-[#426153]" x="316.27" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-778" className="fill-[#426153]" x="328.2" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-779" className="fill-[#426153]" x="280.49" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-780" className="fill-[#426153]" x="292.42" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-781" className="fill-[#426153]" x="304.35" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-782" className="fill-[#426153]" x="316.27" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-783" className="fill-[#426153]" x="328.2" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-784" className="fill-[#426153]" x="292.42" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-785" className="fill-[#426153]" x="304.35" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-786" className="fill-[#426153]" x="316.27" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-787" className="fill-[#426153]" x="328.2" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-788" className="fill-[#426153]" x="292.42" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-789" className="fill-[#426153]" x="310.31" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-790" className="fill-[#426153]" x="322.24" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-791" className="fill-[#426153]" x="334.17" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-792" className="fill-[#426153]" x="304.35" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-793" className="fill-[#426153]" x="316.27" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-794" className="fill-[#426153]" x="328.2" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-795" className="fill-[#426153]" x="298.38" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-796" className="fill-[#426153]" x="310.31" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-797" className="fill-[#426153]" x="322.24" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-798" className="fill-[#426153]" x="334.17" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-799" className="fill-[#426153]" x="304.35" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-800" className="fill-[#426153]" x="316.27" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-801" className="fill-[#426153]" x="328.2" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-802" className="fill-[#426153]" x="280.49" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-803" className="fill-[#426153]" x="310.31" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-804" className="fill-[#426153]" x="322.24" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-805" className="fill-[#426153]" x="334.17" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-806" className="fill-[#426153]" x="292.42" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-807" className="fill-[#426153]" x="322.24" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-808" className="fill-[#426153]" x="334.17" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-809" className="fill-[#426153]" x="328.2" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-810" className="fill-[#426153]" x="220.81" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-811" className="fill-[#426153]" x="232.74" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-812" className="fill-[#426153]" x="244.67" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-813" className="fill-[#426153]" x="101.46" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-814" className="fill-[#426153]" x="113.39" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-815" className="fill-[#426153]" x="125.32" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-816" className="fill-[#426153]" x="137.25" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-817" className="fill-[#426153]" x="149.17" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-818" className="fill-[#426153]" x="101.46" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-819" className="fill-[#426153]" x="113.39" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-820" className="fill-[#426153]" x="125.32" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-821" className="fill-[#426153]" x="137.25" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-822" className="fill-[#426153]" x="149.17" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-823" className="fill-[#426153]" x="107.42" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-824" className="fill-[#426153]" x="119.35" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-825" className="fill-[#426153]" x="131.28" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-826" className="fill-[#426153]" x="143.21" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-827" className="fill-[#426153]" x="155.14" y="196.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-828"
        className="fill-[#426153]"
        x={activeIndex === 2 ? '132.28' : '131.28'}
        y={activeIndex === 2 ? '191.9' : '190.9'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-829" className="fill-[#426153]" x="143.21" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-830" className="fill-[#426153]" x="155.14" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect className="fill-[#426153]" x="137.25" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-831" className="fill-[#426153]" x="149.17" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-832" className="fill-[#426153]" x="143.21" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-833" className="fill-[#426153]" x="155.14" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-834" className="fill-[#426153]" x="59.68" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-835" className="fill-[#426153]" x="59.68" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-836" className="fill-[#426153]" x="71.6" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-837" className="fill-[#426153]" x="53.71" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-838" className="fill-[#426153]" x="65.64" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-839" className="fill-[#426153]" x="77.57" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-840" className="fill-[#426153]" x="47.75" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-841" className="fill-[#426153]" x="65.64" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-842" className="fill-[#426153]" x="47.75" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-843" className="fill-[#426153]" x="59.68" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-844" className="fill-[#426153]" x="53.71" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-845" className="fill-[#426153]" x="65.64" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-846" className="fill-[#426153]" x="131.28" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-847" className="fill-[#426153]" x="137.25" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-848" className="fill-[#426153]" x="167.1" y="323.95" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect className="fill-[#426153]" x="107.48" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-849" className="fill-[#426153]" x="83.53" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-850"
        className="fill-[#426153]"
        x="95.46"
        y={activeIndex === 1 ? '186.12' : '185.12'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-851" className="fill-[#426153]" x="77.57" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-852" className="fill-[#426153]" x="77.57" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-853" className="fill-[#426153]" x="83.53" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-854"
        className="fill-[#426153]"
        x="95.46"
        y={activeIndex === 1 ? '172.56' : '173.56'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-855" className="fill-[#426153]" x="89.5" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-856" className="fill-[#426153]" x="89.5" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-857" className="fill-[#426153]" x="95.46" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-858" className="fill-[#426153]" x="89.5" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-859" className="fill-[#426153]" x="95.46" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-860" className="fill-[#426153]" x="167.1" y="162.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-861" className="fill-[#426153]" x="399.84" y="248.79" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-862" className="fill-[#426153]" x="387.86" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-863" className="fill-[#426153]" x="393.84" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-864" className="fill-[#426153]" x="375.95" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-865" className="fill-[#426153]" x="393.84" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-866" className="fill-[#426153]" x="417.72" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-867" className="fill-[#426153]" x="441.59" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-868" className="fill-[#426153]" x="423.7" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-869" className="fill-[#426153]" x="417.74" y="156.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-870" className="fill-[#426153]" x="393.84" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-871" className="fill-[#426153]" x="369.99" y="11.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-872" className="fill-[#426153]" x="364.02" y="5.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-873" className="fill-[#426153]" x="346.13" y="5.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-874" className="fill-[#426153]" x="340.17" y="11.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-875" className="fill-[#426153]" x="322.25" y="28.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-876" className="fill-[#426153]" x="310.31" y="23.1" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-877" className="fill-[#426153]" x="304.35" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-878" className="fill-[#426153]" x="304.35" y="34.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-879" className="fill-[#426153]" x="298.38" y="69.44" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-880" className="fill-[#426153]" x="286.45" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-881" className="fill-[#426153]" x="268.53" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-882" className="fill-[#426153]" x="280.49" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-883" className="fill-[#426153]" x="274.49" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-884" className="fill-[#426153]" x="256.6" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-885" className="fill-[#426153]" x="226.78" y="28.91" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-886" className="fill-[#426153]" x="232.74" y="23.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-887" className="fill-[#426153]" x="214.85" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-888" className="fill-[#426153]" x="196.92" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-889" className="fill-[#426153]" x="179.03" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-890" className="fill-[#426153]" x="149.17" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-891" className="fill-[#426153]" x="143.31" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-892" className="fill-[#426153]" x="155.14" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-893" className="fill-[#426153]" x="173.07" y="138.81" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-894" className="fill-[#426153]" x="202.89" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-895" className="fill-[#426153]" x="214.81" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-896" className="fill-[#426153]" x="226.78" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-897" className="fill-[#426153]" x="226.78" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-898" className="fill-[#426153]" x="220.81" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-899" className="fill-[#426153]" x="226.78" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-900" className="fill-[#426153]" x="244.67" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-901" className="fill-[#426153]" x="262.56" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-902"
        className="fill-[#426153]"
        x={activeIndex === 4 ? '237.71' : '238.71'}
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-903"
        className="fill-[#426153]"
        x={activeIndex === 4 ? '251.63' : '250.63'}
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-904" className="fill-[#426153]" x="232.74" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-905" className="fill-[#426153]" x="220.81" y="167.86" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-906" className="fill-[#426153]" x="179.03" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-907" className="fill-[#426153]" x="161.14" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-908" className="fill-[#426153]" x="179.03" y="162.03" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-909" className="fill-[#426153]" x="190.96" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-910" className="fill-[#426153]" x="202.88" y="121.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-911" className="fill-[#426153]" x="155.14" y="167.76" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-912" className="fill-[#426153]" x="155.14" y="156.2" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-913" className="fill-[#426153]" x="161.14" y="156.2" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-914" className="fill-[#426153]" x="161.14" y="150.42" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-915" className="fill-[#426153]" x="149.39" y="156.2" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-916" className="fill-[#426153]" x="149.17" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-917" className="fill-[#426153]" x="137.25" y="179.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-918" className="fill-[#426153]" x="83.53" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-919" className="fill-[#426153]" x="65.64" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-920" className="fill-[#426153]" x="71.6" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-921" className="fill-[#426153]" x="71.6" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-922" className="fill-[#426153]" x="23.86" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-923" className="fill-[#426153]" x="17.82" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-924" className="fill-[#426153]" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-925" className="fill-[#426153]" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-926" className="fill-[#426153]" x="41.79" y="306.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-927" className="fill-[#426153]" x="35.78" y="306.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-928" className="fill-[#426153]" x="83.53" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-929" className="fill-[#426153]" x="107.42" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-930" className="fill-[#426153]" x="119.35" y="266.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-931" className="fill-[#426153]" x="143.21" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-932" className="fill-[#426153]" x="53.97" y="23.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-933" className="fill-[#426153]" x="53.79" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-934" className="fill-[#426153]" x="71.6" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-935" className="fill-[#426153]" x="95.46" y="121.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-936" className="fill-[#426153]" x="83.59" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-937" className="fill-[#426153]" x="89.5" y="138.8" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-938" className="fill-[#426153]" x="83.59" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-939" className="fill-[#426153]" x="89.5" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-940" className="fill-[#426153]" x="89.5" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-941" className="fill-[#426153]" x="161.14" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-942" className="fill-[#426153]" x="173.07" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-943" className="fill-[#426153]" x="173.07" y="294.99" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-944" className="fill-[#426153]" x="184.99" y="306.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-945" className="fill-[#426153]" x="184.99" y="318.17" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-946" className="fill-[#426153]" x="173.07" y="318.17" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-947" className="fill-[#426153]" x="161.11" y="323.95" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-948" className="fill-[#426153]" x="155.14" y="318.17" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-949" className="fill-[#426153]" x="179.03" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-950" className="fill-[#426153]" x="196.92" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-951" className="fill-[#426153]" x="208.85" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-952" className="fill-[#426153]" x="268.53" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-953" className="fill-[#426153]" x="274.49" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-954" className="fill-[#426153]" x="232.74" y="295.02" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-955" className="fill-[#426153]" x="244.67" y="295.02" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-956" className="fill-[#426153]" x="232.74" y="306.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-957" className="fill-[#426153]" x="232.74" y="318.17" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-958" className="fill-[#426153]" x="232.74" y="323.96" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-959" className="fill-[#426153]" x="226.78" y="323.96" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-960" className="fill-[#426153]" x="220.81" y="312.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-961" className="fill-[#426153]" x="268.53" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-962" className="fill-[#426153]" x="280.49" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-963" className="fill-[#426153]" x="298.38" y="237.17" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-964" className="fill-[#426153]" x="310.29" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-965" className="fill-[#426153]" x="304.35" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-966" className="fill-[#426153]" x="328.2" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-967" className="fill-[#426153]" x="328.2" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-968" className="fill-[#426153]" x="364.02" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-969"
        className="fill-[#426153]"
        x={activeIndex === 1 ? '102.46' : '101.46'}
        y={activeIndex === 1 ? '172.56' : '173.56'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-970" className="fill-[#426153]" x="101.46" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-971" className="fill-[#426153]" x="95.46" y="190.87" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-972" className="fill-[#426153]" x="47.75" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-973" className="fill-[#426153]" x="41.85" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-974" className="fill-[#426153]" x="53.71" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-975" className="fill-[#426153]" x="71.58" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-976" className="fill-[#426153]" x="304.35" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-977" className="fill-[#426153]" x="35.78" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-978" className="fill-[#426153]" x="411.77" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-979" className="fill-[#426153]" x="411.77" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-980" className="fill-[#426153]" x="435.63" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-981" className="fill-[#426153]" x="417.74" y="138.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-982" className="fill-[#426153]" x="405.81" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-983" className="fill-[#426153]" x="405.81" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-984" className="fill-[#426153]" x="405.81" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-985" className="fill-[#426153]" x="399.84" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-986" className="fill-[#426153]" x="411.77" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-987" className="fill-[#426153]" x="399.84" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-988" className="fill-[#426153]" x="417.74" y="260.35" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-989" className="fill-[#426153]" x="399.84" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-990" className="fill-[#426153]" x="346.13" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-991" className="fill-[#426153]" x="340.17" y="28.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-992" className="fill-[#426153]" x="364.02" y="28.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-993" className="fill-[#426153]" x="358.06" y="23.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-994" className="fill-[#426153]" x="358.06" y="17.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-995" className="fill-[#426153]" x="364.02" y="11.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-996" className="fill-[#426153]" x="340.17" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-997" className="fill-[#426153]" x="364.02" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-998" className="fill-[#426153]" x="387.88" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-999" className="fill-[#426153]" x="352.09" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1000" className="fill-[#426153]" x="375.95" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1001" className="fill-[#426153]" x="346.13" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1002" className="fill-[#426153]" x="369.99" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1003" className="fill-[#426153]" x="340.17" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1004" className="fill-[#426153]" x="364.02" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1005" className="fill-[#426153]" x="340.17" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1006" className="fill-[#426153]" x="364.02" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1007" className="fill-[#426153]" x="340.17" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1008" className="fill-[#426153]" x="364.02" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1009" className="fill-[#426153]" x="346.13" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1010" className="fill-[#426153]" x="369.99" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1011" className="fill-[#426153]" x="352.09" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1012" className="fill-[#426153]" x="340.17" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1013" className="fill-[#426153]" x="364.02" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1014" className="fill-[#426153]" x="358.06" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1015"
        className="fill-[#426153]"
        x="352.09"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1016"
        className="fill-[#426153]"
        x="375.95"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1017"
        className="fill-[#426153]"
        x="340.17"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1018"
        className="fill-[#426153]"
        x="364.02"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1019"
        className="fill-[#426153]"
        x="387.88"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1020"
        className="fill-[#426153]"
        x="352.09"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1021"
        className="fill-[#426153]"
        x="375.95"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1022"
        className="fill-[#426153]"
        x="340.17"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1023"
        className="fill-[#426153]"
        x="364.02"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1024"
        className="fill-[#426153]"
        x="387.88"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1025"
        className="fill-[#426153]"
        x="352.09"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1026"
        className="fill-[#426153]"
        x="375.95"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1027"
        className="fill-[#426153]"
        x="340.17"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1028"
        className="fill-[#426153]"
        x="364.02"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1029"
        className="fill-[#426153]"
        x="387.88"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1030"
        className="fill-[#426153]"
        x="352.09"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1031"
        className="fill-[#426153]"
        x="375.95"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1032"
        className="fill-[#426153]"
        x="340.17"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1033"
        className="fill-[#426153]"
        x="364.02"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1034"
        className="fill-[#426153]"
        x="387.88"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1035"
        className="fill-[#426153]"
        x="352.09"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1036"
        className="fill-[#426153]"
        x="375.95"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1037" className="fill-[#426153]" x="340.17" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1038" className="fill-[#426153]" x="364.02" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1039" className="fill-[#426153]" x="387.88" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1040"
        className="fill-[#426153]"
        x="358.06"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1041"
        className="fill-[#426153]"
        x="381.92"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1042"
        className="fill-[#426153]"
        x="352.09"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1043"
        className="fill-[#426153]"
        x="375.95"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1044"
        className="fill-[#426153]"
        x="358.06"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1045" className="fill-[#426153]" x="346.13" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1046" className="fill-[#426153]" x="369.99" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1047"
        className="fill-[#426153]"
        x="358.06"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1048"
        className="fill-[#426153]"
        x="346.13"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1049"
        className="fill-[#426153]"
        x="369.99"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1050"
        className="fill-[#426153]"
        x="340.17"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1051"
        className="fill-[#426153]"
        x="364.02"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1052"
        className="fill-[#426153]"
        x="346.13"
        y="167.78"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1053"
        className="fill-[#426153]"
        x="369.99"
        y="167.78"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1054" className="fill-[#426153]" x="346.13" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1055" className="fill-[#426153]" x="369.99" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1056"
        className="fill-[#426153]"
        x="346.13"
        y="156.22"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1057"
        className="fill-[#426153]"
        x="369.99"
        y="156.22"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1058"
        className="fill-[#426153]"
        x="393.84"
        y="156.22"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1059"
        className="fill-[#426153]"
        x="393.84"
        y="266.09"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1060"
        className="fill-[#426153]"
        x="381.92"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1061"
        className="fill-[#426153]"
        x="364.02"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1062"
        className="fill-[#426153]"
        x="387.88"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1063"
        className="fill-[#426153]"
        x="358.06"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1064"
        className="fill-[#426153]"
        x="381.92"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1065"
        className="fill-[#426153]"
        x="340.17"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1066"
        className="fill-[#426153]"
        x="364.02"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1067"
        className="fill-[#426153]"
        x="387.88"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1068"
        className="fill-[#426153]"
        x="352.09"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1069"
        className="fill-[#426153]"
        x="375.95"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1070"
        className="fill-[#426153]"
        x="346.13"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1071"
        className="fill-[#426153]"
        x="369.99"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1072"
        className="fill-[#426153]"
        x="346.13"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1073"
        className="fill-[#426153]"
        x="369.99"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1074"
        className="fill-[#426153]"
        x="346.13"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1075"
        className="fill-[#426153]"
        x="369.99"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1076"
        className="fill-[#426153]"
        x="346.13"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1077"
        className="fill-[#426153]"
        x="369.99"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1078"
        className="fill-[#426153]"
        x="393.84"
        y="277.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1079" className="fill-[#426153]" x="322.24" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1080" className="fill-[#426153]" x="334.17" y="28.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1081"
        className="fill-[#426153]"
        x="298.38"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1082"
        className="fill-[#426153]"
        x="322.24"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1083"
        className="fill-[#426153]"
        x="286.45"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1084"
        className="fill-[#426153]"
        x="310.31"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1085"
        className="fill-[#426153]"
        x="334.17"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1086"
        className="fill-[#426153]"
        x="298.38"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1087"
        className="fill-[#426153]"
        x="322.24"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1088"
        className="fill-[#426153]"
        x="286.45"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1089"
        className="fill-[#426153]"
        x="310.31"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1090"
        className="fill-[#426153]"
        x="334.17"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1091"
        className="fill-[#426153]"
        x="298.38"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1092"
        className="fill-[#426153]"
        x="322.24"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1093"
        className="fill-[#426153]"
        x="286.45"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1094"
        className="fill-[#426153]"
        x="310.31"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1095"
        className="fill-[#426153]"
        x="334.17"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1096"
        className="fill-[#426153]"
        x="298.38"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1097"
        className="fill-[#426153]"
        x="322.24"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1098"
        className="fill-[#426153]"
        x="286.45"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1099"
        className="fill-[#426153]"
        x="310.31"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1100"
        className="fill-[#426153]"
        x="334.17"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1101"
        className="fill-[#426153]"
        x="298.38"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1102"
        className="fill-[#426153]"
        x="322.24"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1103" className="fill-[#426153]" x="286.45" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1104" className="fill-[#426153]" x="310.31" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1105" className="fill-[#426153]" x="334.17" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1106"
        className="fill-[#426153]"
        x="298.38"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1107"
        className="fill-[#426153]"
        x="322.24"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1108"
        className="fill-[#426153]"
        x="286.45"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1109"
        className="fill-[#426153]"
        x="310.31"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1110"
        className="fill-[#426153]"
        x="334.17"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1111"
        className="fill-[#426153]"
        x="298.38"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1112"
        className="fill-[#426153]"
        x="322.24"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1113" className="fill-[#426153]" x="286.45" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1114" className="fill-[#426153]" x="310.31" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1115" className="fill-[#426153]" x="334.17" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1116"
        className="fill-[#426153]"
        x="298.38"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1117"
        className="fill-[#426153]"
        x="322.24"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1118"
        className="fill-[#426153]"
        x="286.45"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1119"
        className="fill-[#426153]"
        x="310.31"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1120"
        className="fill-[#426153]"
        x="334.17"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1121"
        className="fill-[#426153]"
        x="298.38"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1122"
        className="fill-[#426153]"
        x="322.24"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1123"
        className="fill-[#426153]"
        x="286.45"
        y="167.78"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1124"
        className="fill-[#426153]"
        x="310.31"
        y="167.78"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1125"
        className="fill-[#426153]"
        x="334.17"
        y="167.78"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1126" className="fill-[#426153]" x="298.38" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1127" className="fill-[#426153]" x="322.24" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1128"
        className="fill-[#426153]"
        x="286.45"
        y="156.22"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1129"
        className="fill-[#426153]"
        x="310.31"
        y="156.22"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1130"
        className="fill-[#426153]"
        x="334.17"
        y="156.22"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1131"
        className="fill-[#426153]"
        x="286.45"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1132"
        className="fill-[#426153]"
        x="310.31"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1133"
        className="fill-[#426153]"
        x="292.42"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1134"
        className="fill-[#426153]"
        x="316.27"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1135"
        className="fill-[#426153]"
        x="298.38"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1136"
        className="fill-[#426153]"
        x="322.24"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1137"
        className="fill-[#426153]"
        x="286.45"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1138"
        className="fill-[#426153]"
        x="310.31"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1139"
        className="fill-[#426153]"
        x="334.17"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1140" className="fill-[#426153]" x="244.67" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1141" className="fill-[#426153]" x="268.53" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1142" className="fill-[#426153]" x="244.67" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1143" className="fill-[#426153]" x="268.53" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1144" className="fill-[#426153]" x="244.67" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1145" className="fill-[#426153]" x="268.53" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1146" className="fill-[#426153]" x="250.63" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1147" className="fill-[#426153]" x="238.71" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1148" className="fill-[#426153]" x="262.56" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1149" className="fill-[#426153]" x="232.74" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1150" className="fill-[#426153]" x="256.6" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1151" className="fill-[#426153]" x="232.74" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1152" className="fill-[#426153]" x="256.6" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1153" className="fill-[#426153]" x="238.71" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1154" className="fill-[#426153]" x="262.56" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1155" className="fill-[#426153]" x="226.78" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1156" className="fill-[#426153]" x="250.63" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1157" className="fill-[#426153]" x="274.49" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1158" className="fill-[#426153]" x="238.71" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1159" className="fill-[#426153]" x="262.56" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1160"
        className="fill-[#426153]"
        x="226.78"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1161"
        className="fill-[#426153]"
        x="250.63"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1162"
        className="fill-[#426153]"
        x="274.49"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1163" className="fill-[#426153]" x="256.6" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1164"
        className="fill-[#426153]"
        x="238.71"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1165"
        className="fill-[#426153]"
        x="262.56"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1166"
        className="fill-[#426153]"
        x="244.67"
        y={activeIndex === 4 ? '134.05' : '133.05'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1167"
        className="fill-[#426153]"
        x="268.53"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1168"
        className="fill-[#426153]"
        x="268.53"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1169"
        className="fill-[#426153]"
        x="226.78"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1170"
        className="fill-[#426153]"
        x="274.49"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1171"
        className="fill-[#426153]"
        x="244.67"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1172"
        className="fill-[#426153]"
        x="268.53"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1173"
        className="fill-[#426153]"
        x="238.71"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1174"
        className="fill-[#426153]"
        x="262.56"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1175" className="fill-[#426153]" x="226.78" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1176" className="fill-[#426153]" x="250.63" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1177" className="fill-[#426153]" x="274.49" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1178"
        className="fill-[#426153]"
        x="238.71"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1179"
        className="fill-[#426153]"
        x="262.56"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1180"
        className="fill-[#426153]"
        x={activeIndex === 3 ? '227.78' : '226.78'}
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1181"
        className="fill-[#426153]"
        x="250.63"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1182"
        className="fill-[#426153]"
        x="274.49"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1183"
        className="fill-[#426153]"
        x="238.71"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1184"
        className="fill-[#426153]"
        x="262.56"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1185" className="fill-[#426153]" x="226.78" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1186" className="fill-[#426153]" x="250.63" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1187" className="fill-[#426153]" x="274.49" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1188"
        className="fill-[#426153]"
        x="238.71"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1189"
        className="fill-[#426153]"
        x="262.56"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1190"
        className="fill-[#426153]"
        x="226.78"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1191"
        className="fill-[#426153]"
        x="250.63"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1192"
        className="fill-[#426153]"
        x="274.49"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1193"
        className="fill-[#426153]"
        x="238.71"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1194"
        className="fill-[#426153]"
        x="262.56"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1195"
        className="fill-[#426153]"
        x="232.74"
        y="167.78"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1196" className="fill-[#426153]" x="256.6" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1197" className="fill-[#426153]" x="226.78" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1198" className="fill-[#426153]" x="250.63" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1199" className="fill-[#426153]" x="274.49" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1200"
        className="fill-[#426153]"
        x="232.74"
        y="156.22"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1201" className="fill-[#426153]" x="256.6" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1202"
        className="fill-[#426153]"
        x="220.81"
        y="266.09"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1203"
        className="fill-[#426153]"
        x="244.67"
        y="266.09"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1204"
        className="fill-[#426153]"
        x="268.53"
        y="266.09"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1205"
        className="fill-[#426153]"
        x="238.71"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1206"
        className="fill-[#426153]"
        x="262.56"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1207"
        className="fill-[#426153]"
        x="238.71"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1208"
        className="fill-[#426153]"
        x="262.56"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1209"
        className="fill-[#426153]"
        x="232.74"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1210" className="fill-[#426153]" x="256.6" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1211"
        className="fill-[#426153]"
        x="226.78"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1212"
        className="fill-[#426153]"
        x="250.63"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1213"
        className="fill-[#426153]"
        x="274.49"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1214"
        className="fill-[#426153]"
        x="238.71"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1215"
        className="fill-[#426153]"
        x="262.56"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1216"
        className="fill-[#426153]"
        x="226.78"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1217"
        className="fill-[#426153]"
        x="250.63"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1218"
        className="fill-[#426153]"
        x="274.49"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1219"
        className="fill-[#426153]"
        x="238.71"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1220"
        className="fill-[#426153]"
        x="262.56"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1221"
        className="fill-[#426153]"
        x="226.78"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1222"
        className="fill-[#426153]"
        x="250.63"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1223"
        className="fill-[#426153]"
        x="274.49"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1224"
        className="fill-[#426153]"
        x="238.71"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1225"
        className="fill-[#426153]"
        x="262.56"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1226" className="fill-[#426153]" x="173.07" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1227" className="fill-[#426153]" x="196.92" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1228" className="fill-[#426153]" x="184.99" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1229" className="fill-[#426153]" x="208.85" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1230" className="fill-[#426153]" x="202.89" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1231" className="fill-[#426153]" x="190.96" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1232" className="fill-[#426153]" x="214.81" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1233" className="fill-[#426153]" x="208.85" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1234" className="fill-[#426153]" x="202.89" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1235" className="fill-[#426153]" x="202.89" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1236" className="fill-[#426153]" x="208.85" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1237" className="fill-[#426153]" x="214.81" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1238"
        className="fill-[#426153]"
        x="179.03"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1239"
        className="fill-[#426153]"
        x="184.99"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1240"
        className="fill-[#426153]"
        x="184.99"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1241"
        className="fill-[#426153]"
        x="190.96"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1242"
        className="fill-[#426153]"
        x="179.03"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1243"
        className="fill-[#426153]"
        x="161.14"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1244"
        className="fill-[#426153]"
        x="184.99"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1245" className="fill-[#426153]" x="167.1" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1246"
        className="fill-[#426153]"
        x="190.96"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1247"
        className="fill-[#426153]"
        x="173.07"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1248"
        className="fill-[#426153]"
        x="196.92"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1249" className="fill-[#426153]" x="179.03" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1250"
        className="fill-[#426153]"
        x="161.14"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1251"
        className="fill-[#426153]"
        x="184.99"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1252"
        className="fill-[#426153]"
        x="208.85"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1253"
        className="fill-[#426153]"
        x="173.07"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1254"
        className="fill-[#426153]"
        x="196.92"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1255"
        className="fill-[#426153]"
        x="161.14"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1256"
        className="fill-[#426153]"
        x="208.85"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1257" className="fill-[#426153]" x="173.07" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1258" className="fill-[#426153]" x="196.92" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1259"
        className="fill-[#426153]"
        x="161.14"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1260"
        className="fill-[#426153]"
        x="184.99"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1261"
        className="fill-[#426153]"
        x="208.85"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1262"
        className="fill-[#426153]"
        x="173.07"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1263"
        className="fill-[#426153]"
        x="202.89"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1264"
        className="fill-[#426153]"
        x="214.81"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1265"
        className="fill-[#426153]"
        x="196.92"
        y="266.09"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1266"
        className="fill-[#426153]"
        x="184.99"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1267"
        className="fill-[#426153]"
        x="208.85"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1268"
        className="fill-[#426153]"
        x="190.96"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1269"
        className="fill-[#426153]"
        x="214.81"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1270"
        className="fill-[#426153]"
        x="179.03"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1271"
        className="fill-[#426153]"
        x="202.89"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1272" className="fill-[#426153]" x="167.1" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1273"
        className="fill-[#426153]"
        x="190.96"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1274"
        className="fill-[#426153]"
        x="214.81"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1275"
        className="fill-[#426153]"
        x="179.03"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1276"
        className="fill-[#426153]"
        x="202.89"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1277" className="fill-[#426153]" x="167.1" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1278"
        className="fill-[#426153]"
        x="190.96"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1279"
        className="fill-[#426153]"
        x="214.81"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1280"
        className="fill-[#426153]"
        x="179.03"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1281"
        className="fill-[#426153]"
        x="202.89"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1282" className="fill-[#426153]" x="167.1" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1283"
        className="fill-[#426153]"
        x="190.96"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1284"
        className="fill-[#426153]"
        x="214.81"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1285"
        className="fill-[#426153]"
        x="179.03"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1286"
        className="fill-[#426153]"
        x="202.89"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1287"
        className="fill-[#426153]"
        x="214.81"
        y="300.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1288"
        className="fill-[#426153]"
        x="196.93"
        y="300.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1289"
        className="fill-[#426153]"
        x="214.81"
        y="289.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1290"
        className="fill-[#426153]"
        x="214.81"
        y="283.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1291"
        className="fill-[#426153]"
        x="208.85"
        y="277.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1292"
        className="fill-[#426153]"
        x="202.89"
        y="271.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1293"
        className="fill-[#426153]"
        x="149.17"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1294"
        className="fill-[#426153]"
        x="155.14"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1295"
        className="fill-[#426153]"
        x="113.61"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1296"
        className="fill-[#426153]"
        x="155.14"
        y="271.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1297" className="fill-[#426153]" x="89.5" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1298" className="fill-[#426153]" x="53.71" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1299" className="fill-[#426153]" x="77.57" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1300" className="fill-[#426153]" x="47.75" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1301" className="fill-[#426153]" x="71.6" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1302" className="fill-[#426153]" x="95.46" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1303" className="fill-[#426153]" x="59.68" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1304" className="fill-[#426153]" x="83.53" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1305" className="fill-[#426153]" x="71.6" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1306" className="fill-[#426153]" x="95.46" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1307" className="fill-[#426153]" x="95.46" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1308" className="fill-[#426153]" x="95.46" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1309" className="fill-[#426153]" x="95.46" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1310" className="fill-[#426153]" x="95.46" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1311" className="fill-[#426153]" x="95.46" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1312" className="fill-[#426153]" x="95.46" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1313" className="fill-[#426153]" x="41.79" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1314" className="fill-[#426153]" x="41.79" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1315" className="fill-[#426153]" x="41.79" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1316" className="fill-[#426153]" x="65.64" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1317" className="fill-[#426153]" x="59.68" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1318" className="fill-[#426153]" x="41.79" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1319" className="fill-[#426153]" x="65.64" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1320" className="fill-[#426153]" x="89.5" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1321" className="fill-[#426153]" x="29.82" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1322" className="fill-[#426153]" x="23.86" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1323" className="fill-[#426153]" x="23.86" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1324" className="fill-[#426153]" x="23.86" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1325" className="fill-[#426153]" x="29.82" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1326" className="fill-[#426153]" x="35.78" y="300.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1327" className="fill-[#426153]" x="35.78" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1328" className="fill-[#426153]" x="23.86" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1329" className="fill-[#426153]" x="11.93" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1330" className="fill-[#426153]" x="35.78" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1331" className="fill-[#426153]" x="23.86" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1332" className="fill-[#426153]" x="11.93" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1333" className="fill-[#426153]" x="35.78" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1334"
        className="fill-[#426153]"
        x="119.35"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1335"
        className="fill-[#426153]"
        x="101.46"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1336"
        className="fill-[#426153]"
        x="125.32"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1337"
        className="fill-[#426153]"
        x="149.17"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1338"
        className="fill-[#426153]"
        x="113.39"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1339"
        className="fill-[#426153]"
        x="137.25"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1340"
        className="fill-[#426153]"
        x="101.46"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1341"
        className="fill-[#426153]"
        x="131.28"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1342"
        className="fill-[#426153]"
        x="149.17"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1343"
        className="fill-[#426153]"
        x="113.39"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1344"
        className="fill-[#426153]"
        x="137.25"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1345"
        className="fill-[#426153]"
        x="101.46"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1346"
        className="fill-[#426153]"
        x="125.32"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1347"
        className="fill-[#426153]"
        x="149.17"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1348"
        className="fill-[#426153]"
        x="113.39"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1349"
        className="fill-[#426153]"
        x="137.25"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1350"
        className="fill-[#426153]"
        x="101.46"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1351"
        className="fill-[#426153]"
        x="125.32"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1352"
        className="fill-[#426153]"
        x="149.17"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1353"
        className="fill-[#426153]"
        x="113.39"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1354"
        className="fill-[#426153]"
        x="137.25"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1355"
        className="fill-[#426153]"
        x="226.78"
        y="312.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1356"
        className="fill-[#426153]"
        x="226.78"
        y="300.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1357"
        className="fill-[#426153]"
        x="226.78"
        y="289.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1358"
        className="fill-[#426153]"
        x="250.63"
        y="289.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1359"
        className="fill-[#426153]"
        x="226.78"
        y="283.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1360"
        className="fill-[#426153]"
        x="250.63"
        y="283.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1361"
        className="fill-[#426153]"
        x="226.78"
        y="277.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1362"
        className="fill-[#426153]"
        x="250.63"
        y="277.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1363"
        className="fill-[#426153]"
        x="232.74"
        y="271.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1364" className="fill-[#426153]" x="256.6" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1365" className="fill-[#426153]" x="286.45" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1366" className="fill-[#426153]" x="310.31" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1367" className="fill-[#426153]" x="334.17" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1368" className="fill-[#426153]" x="298.38" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1369" className="fill-[#426153]" x="322.24" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1370" className="fill-[#426153]" x="298.38" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1371" className="fill-[#426153]" x="322.24" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1372" className="fill-[#426153]" x="298.38" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1373" className="fill-[#426153]" x="328.2" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1374" className="fill-[#426153]" x="310.31" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1375" className="fill-[#426153]" x="334.17" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1376" className="fill-[#426153]" x="316.27" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1377" className="fill-[#426153]" x="298.38" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1378" className="fill-[#426153]" x="322.24" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1379" className="fill-[#426153]" x="286.45" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1380" className="fill-[#426153]" x="328.2" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1381" className="fill-[#426153]" x="316.27" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1382" className="fill-[#426153]" x="322.24" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1383" className="fill-[#426153]" x="226.78" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1384" className="fill-[#426153]" x="238.71" y="28.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1385"
        className="fill-[#426153]"
        x="119.35"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1386"
        className="fill-[#426153]"
        x="143.21"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1387"
        className="fill-[#426153]"
        x="107.42"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1388"
        className="fill-[#426153]"
        x="131.28"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1389"
        className="fill-[#426153]"
        x="155.14"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1390"
        className="fill-[#426153]"
        x="125.32"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1391"
        className="fill-[#426153]"
        x="149.17"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1392" className="fill-[#426153]" x="137.25" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1393"
        className="fill-[#426153]"
        x={activeIndex === 2 ? '132.28' : '131.28'}
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1394"
        className="fill-[#426153]"
        x="155.14"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1395" className="fill-[#426153]" x="53.97" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1396" className="fill-[#426153]" x="65.64" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1397" className="fill-[#426153]" x="59.68" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1398" className="fill-[#426153]" x="53.71" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1399" className="fill-[#426153]" x="65.64" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1400" className="fill-[#426153]" x="65.64" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1401"
        className="fill-[#426153]"
        x="131.28"
        y="300.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1402"
        className="fill-[#426153]"
        x="137.25"
        y="289.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1403" className="fill-[#426153]" x="71.6" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        className="fill-[#426153]"
        x={activeIndex === 1 ? '88.5' : '89.5'}
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1404" className="fill-[#426153]" x="71.6" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1405"
        className="fill-[#426153]"
        x={activeIndex === 1 ? '88.5' : '89.5'}
        y={activeIndex === 1 ? '172.56' : '173.56'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1406" className="fill-[#426153]" x="95.46" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1407" className="fill-[#426153]" x="95.46" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1408" className="fill-[#426153]" x="89.5" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1409"
        className="fill-[#426153]"
        x="393.84"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1410"
        className="fill-[#426153]"
        x="375.95"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1411"
        className="fill-[#426153]"
        x="417.72"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1412"
        className="fill-[#426153]"
        x="441.59"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1413" className="fill-[#426153]" x="393.84" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1414" className="fill-[#426153]" x="369.99" y="23.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1415" className="fill-[#426153]" x="340.17" y="17.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1416" className="fill-[#426153]" x="328.2" y="23.1" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1417" className="fill-[#426153]" x="304.35" y="40.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1418" className="fill-[#426153]" x="286.45" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1419" className="fill-[#426153]" x="274.49" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1420" className="fill-[#426153]" x="250.63" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1421" className="fill-[#426153]" x="232.74" y="28.91" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1422" className="fill-[#426153]" x="202.89" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1423" className="fill-[#426153]" x="155.14" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1424"
        className="fill-[#426153]"
        x="161.14"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1425"
        className="fill-[#426153]"
        x="173.07"
        y="144.58"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1426" className="fill-[#426153]" x="220.81" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1427"
        className="fill-[#426153]"
        x="220.81"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1428"
        className="fill-[#426153]"
        x="232.74"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1429"
        className="fill-[#426153]"
        x="262.56"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1430"
        className="fill-[#426153]"
        x="232.74"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1431"
        className="fill-[#426153]"
        x="202.89"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1432" className="fill-[#426153]" x="167.1" y="161.72" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1433"
        className="fill-[#426153]"
        x="202.88"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1434"
        className="fill-[#426153]"
        x="155.14"
        y="161.98"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1435"
        className="fill-[#426153]"
        x="161.14"
        y="162.03"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1436"
        className="fill-[#426153]"
        x="149.39"
        y="150.42"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1437"
        className="fill-[#426153]"
        x={activeIndex === 2 ? '118.35' : '119.35'}
        y={activeIndex === 2 ? '191.9' : '190.9'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1438" className="fill-[#426153]" x="77.57" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1439" className="fill-[#426153]" x="71.6" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1440" className="fill-[#426153]" x="17.82" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1441" className="fill-[#426153]" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1442" className="fill-[#426153]" x="17.89" y="306.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1443"
        className="fill-[#426153]"
        x="101.46"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1444"
        className="fill-[#426153]"
        x="131.28"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1445" className="fill-[#426153]" x="59.68" y="23.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1446" className="fill-[#426153]" x="89.5" y="121.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1447" className="fill-[#426153]" x="83.59" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1448" className="fill-[#426153]" x="89.5" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1449"
        className="fill-[#426153]"
        x="161.14"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1450" className="fill-[#426153]" x="167.1" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1451"
        className="fill-[#426153]"
        x="184.99"
        y="312.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1452"
        className="fill-[#426153]"
        x="173.07"
        y="329.78"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1453"
        className="fill-[#426153]"
        x="173.07"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1454"
        className="fill-[#426153]"
        x="208.85"
        y="300.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1455"
        className="fill-[#426153]"
        x="268.53"
        y="289.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1456"
        className="fill-[#426153]"
        x="238.71"
        y="295.02"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1457"
        className="fill-[#426153]"
        x="232.74"
        y="312.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1458"
        className="fill-[#426153]"
        x="232.74"
        y="329.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1459"
        className="fill-[#426153]"
        x="214.81"
        y="306.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1460"
        className="fill-[#426153]"
        x="292.42"
        y="237.17"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1461"
        className="fill-[#426153]"
        x="316.27"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1462" className="fill-[#426153]" x="328.2" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1463"
        className="fill-[#426153]"
        x="369.99"
        y="266.09"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1464"
        className="fill-[#426153]"
        x="101.46"
        y="190.87"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1465" className="fill-[#426153]" x="53.71" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1466" className="fill-[#426153]" x="71.58" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1467"
        className="fill-[#426153]"
        x="399.84"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1468" className="fill-[#426153]" x="423.7" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1469"
        className="fill-[#426153]"
        x="429.66"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1470"
        className="fill-[#426153]"
        x="399.84"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1471"
        className="fill-[#426153]"
        x="399.84"
        y="266.09"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1472"
        className="fill-[#426153]"
        x="411.77"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1473"
        className="fill-[#426153]"
        x="414.63"
        y="275.42"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1474" className="fill-[#426153]" x="352.09" y="28.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1475" className="fill-[#426153]" x="346.13" y="17.34" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1476" className="fill-[#426153]" x="358.06" y="5.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1477" className="fill-[#426153]" x="375.95" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1478" className="fill-[#426153]" x="364.02" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1479" className="fill-[#426153]" x="358.06" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1480" className="fill-[#426153]" x="352.09" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1481" className="fill-[#426153]" x="352.09" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1482" className="fill-[#426153]" x="352.09" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1483" className="fill-[#426153]" x="358.06" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1484" className="fill-[#426153]" x="364.02" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1485" className="fill-[#426153]" x="346.13" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1486"
        className="fill-[#426153]"
        x="364.02"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1487"
        className="fill-[#426153]"
        x="352.09"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1488"
        className="fill-[#426153]"
        x="340.17"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1489"
        className="fill-[#426153]"
        x="387.88"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1490"
        className="fill-[#426153]"
        x="375.95"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1491"
        className="fill-[#426153]"
        x="364.02"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1492"
        className="fill-[#426153]"
        x="352.09"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1493"
        className="fill-[#426153]"
        x="340.17"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1494"
        className="fill-[#426153]"
        x="387.88"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1495"
        className="fill-[#426153]"
        x="375.95"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1496"
        className="fill-[#426153]"
        x="364.02"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1497" className="fill-[#426153]" x="352.09" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1498"
        className="fill-[#426153]"
        x="346.13"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1499"
        className="fill-[#426153]"
        x="340.17"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1500"
        className="fill-[#426153]"
        x="346.13"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1501" className="fill-[#426153]" x="358.06" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1502"
        className="fill-[#426153]"
        x="369.99"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1503"
        className="fill-[#426153]"
        x="381.87"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1504"
        className="fill-[#426153]"
        x="375.95"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1505"
        className="fill-[#426153]"
        x="381.92"
        y="167.78"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1506" className="fill-[#426153]" x="381.92" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1507"
        className="fill-[#426153]"
        x="381.92"
        y="156.22"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1508"
        className="fill-[#426153]"
        x="369.99"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1509"
        className="fill-[#426153]"
        x="375.95"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1510"
        className="fill-[#426153]"
        x="369.99"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1511"
        className="fill-[#426153]"
        x="352.09"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1512"
        className="fill-[#426153]"
        x="340.17"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1513"
        className="fill-[#426153]"
        x="387.88"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1514"
        className="fill-[#426153]"
        x="381.92"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1515"
        className="fill-[#426153]"
        x="381.92"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1516"
        className="fill-[#426153]"
        x="381.92"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1517"
        className="fill-[#426153]"
        x="381.92"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1518" className="fill-[#426153]" x="334.17" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1519"
        className="fill-[#426153]"
        x="310.31"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1520"
        className="fill-[#426153]"
        x="298.38"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1521"
        className="fill-[#426153]"
        x="286.45"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1522"
        className="fill-[#426153]"
        x="334.17"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1523"
        className="fill-[#426153]"
        x="322.24"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1524"
        className="fill-[#426153]"
        x="310.31"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1525"
        className="fill-[#426153]"
        x="298.38"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1526"
        className="fill-[#426153]"
        x="286.45"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1527"
        className="fill-[#426153]"
        x="334.17"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1528"
        className="fill-[#426153]"
        x="322.24"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1529"
        className="fill-[#426153]"
        x="310.31"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1530" className="fill-[#426153]" x="298.38" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1531"
        className="fill-[#426153]"
        x="286.45"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1532"
        className="fill-[#426153]"
        x="334.17"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1533"
        className="fill-[#426153]"
        x="322.24"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1534"
        className="fill-[#426153]"
        x="310.31"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1535" className="fill-[#426153]" x="298.38" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1536"
        className="fill-[#426153]"
        x="286.45"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1537"
        className="fill-[#426153]"
        x="334.17"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1538"
        className="fill-[#426153]"
        x="322.24"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1539"
        className="fill-[#426153]"
        x="310.31"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1540"
        className="fill-[#426153]"
        x="298.38"
        y="167.78"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1541" className="fill-[#426153]" x="286.45" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1542" className="fill-[#426153]" x="334.17" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1543"
        className="fill-[#426153]"
        x="322.24"
        y="156.22"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1544"
        className="fill-[#426153]"
        x="298.38"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1545"
        className="fill-[#426153]"
        x="304.35"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1546"
        className="fill-[#426153]"
        x="310.31"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1547"
        className="fill-[#426153]"
        x="298.38"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1548" className="fill-[#426153]" x="232.74" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1549" className="fill-[#426153]" x="232.74" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1550" className="fill-[#426153]" x="232.74" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1551" className="fill-[#426153]" x="238.71" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1552" className="fill-[#426153]" x="250.63" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1553" className="fill-[#426153]" x="244.67" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1554" className="fill-[#426153]" x="244.67" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1555" className="fill-[#426153]" x="250.63" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1556" className="fill-[#426153]" x="238.71" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1557" className="fill-[#426153]" x="226.78" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1558"
        className="fill-[#426153]"
        x="226.78"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1559"
        className="fill-[#426153]"
        x="262.56"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1560"
        className="fill-[#426153]"
        x="268.53"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1561"
        className="fill-[#426153]"
        x="274.49"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1562" className="fill-[#426153]" x="256.6" y="127.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1563"
        className="fill-[#426153]"
        x="238.71"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1564" className="fill-[#426153]" x="256.6" y="109.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1565"
        className="fill-[#426153]"
        x="250.63"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1566" className="fill-[#426153]" x="238.71" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1567"
        className="fill-[#426153]"
        x={activeIndex === 3 ? '227.78' : '226.78'}
        y={activeIndex === 3 ? '209.24' : '208.24'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1568"
        className="fill-[#426153]"
        x="274.49"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1569"
        className="fill-[#426153]"
        x="262.56"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1570"
        className="fill-[#426153]"
        x="250.63"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1571" className="fill-[#426153]" x="238.71" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1572"
        className="fill-[#426153]"
        x="226.78"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1573"
        className="fill-[#426153]"
        x="274.49"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1574"
        className="fill-[#426153]"
        x="262.56"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1575"
        className="fill-[#426153]"
        x="250.63"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1576"
        className="fill-[#426153]"
        x="244.67"
        y="167.78"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1577" className="fill-[#426153]" x="238.71" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1578"
        className="fill-[#426153]"
        x="220.81"
        y="161.99"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1579"
        className="fill-[#426153]"
        x="268.53"
        y="156.22"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1580" className="fill-[#426153]" x="256.6" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1581"
        className="fill-[#426153]"
        x="250.63"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1582"
        className="fill-[#426153]"
        x="250.63"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1583"
        className="fill-[#426153]"
        x="244.67"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1584"
        className="fill-[#426153]"
        x="238.71"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1585"
        className="fill-[#426153]"
        x="226.78"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1586"
        className="fill-[#426153]"
        x="274.49"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1587"
        className="fill-[#426153]"
        x="262.56"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1588"
        className="fill-[#426153]"
        x="250.63"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1589"
        className="fill-[#426153]"
        x="238.71"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1590"
        className="fill-[#426153]"
        x="226.78"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1591"
        className="fill-[#426153]"
        x="274.49"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1592" className="fill-[#426153]" x="173.07" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1593" className="fill-[#426153]" x="190.96" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1594" className="fill-[#426153]" x="202.89" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1595" className="fill-[#426153]" x="190.96" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1596" className="fill-[#426153]" x="214.81" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1597"
        className="fill-[#426153]"
        x="184.99"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1598"
        className="fill-[#426153]"
        x="173.07"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1599"
        className="fill-[#426153]"
        x="161.14"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1600"
        className="fill-[#426153]"
        x="173.07"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1601"
        className="fill-[#426153]"
        x="179.03"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1602"
        className="fill-[#426153]"
        x="184.99"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1603" className="fill-[#426153]" x="190.96" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1604"
        className="fill-[#426153]"
        x="196.92"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1605"
        className="fill-[#426153]"
        x="184.99"
        y={activeIndex === 0 ? '203.46' : '202.46'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1606"
        className="fill-[#426153]"
        x="173.07"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1607" className="fill-[#426153]" x="161.14" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1608" className="fill-[#426153]" x="208.85" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1609"
        className="fill-[#426153]"
        x="196.92"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1610"
        className="fill-[#426153]"
        x="190.96"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1611"
        className="fill-[#426153]"
        x="184.99"
        y="266.09"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1612"
        className="fill-[#426153]"
        x="196.92"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1613"
        className="fill-[#426153]"
        x="202.89"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1614"
        className="fill-[#426153]"
        x="190.96"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1615"
        className="fill-[#426153]"
        x="179.03"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1616" className="fill-[#426153]" x="167.1" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1617"
        className="fill-[#426153]"
        x="214.81"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1618"
        className="fill-[#426153]"
        x="202.89"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1619"
        className="fill-[#426153]"
        x="190.96"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1620"
        className="fill-[#426153]"
        x="179.03"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1621" className="fill-[#426153]" x="167.1" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1622"
        className="fill-[#426153]"
        x="214.81"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1623"
        className="fill-[#426153]"
        x="173.07"
        y="289.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1624" className="fill-[#426153]" x="167.1" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1625"
        className="fill-[#426153]"
        x="214.81"
        y="271.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1626"
        className="fill-[#426153]"
        x="155.14"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1627" className="fill-[#426153]" x="71.6" y="208.24" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1628" className="fill-[#426153]" x="65.64" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1629" className="fill-[#426153]" x="59.68" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1630" className="fill-[#426153]" x="47.75" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1631" className="fill-[#426153]" x="95.46" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1632" className="fill-[#426153]" x="83.53" y="242.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1633" className="fill-[#426153]" x="83.53" y="231.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1634" className="fill-[#426153]" x="83.53" y="219.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1635" className="fill-[#426153]" x="47.75" y="300.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1636" className="fill-[#426153]" x="53.71" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1637" className="fill-[#426153]" x="47.75" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1638" className="fill-[#426153]" x="53.71" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1639" className="fill-[#426153]" x="17.89" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1640" className="fill-[#426153]" x="35.78" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1641" className="fill-[#426153]" x="35.78" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1642" className="fill-[#426153]" x="23.86" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1643" className="fill-[#426153]" x="35.78" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1644" className="fill-[#426153]" x="11.93" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1645" className="fill-[#426153]" x="23.86" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1646"
        className="fill-[#426153]"
        x="149.17"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1647"
        className="fill-[#426153]"
        x="137.25"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1648"
        className="fill-[#426153]"
        x="125.32"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1649"
        className="fill-[#426153]"
        x="113.39"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1650"
        className="fill-[#426153]"
        x="101.46"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1651"
        className="fill-[#426153]"
        x="149.17"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1652"
        className="fill-[#426153]"
        x="137.25"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1653"
        className="fill-[#426153]"
        x="125.32"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1654"
        className="fill-[#426153]"
        x="113.39"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1655"
        className="fill-[#426153]"
        x="101.46"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1656"
        className="fill-[#426153]"
        x="149.17"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1657"
        className="fill-[#426153]"
        x="226.78"
        y="295.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1658"
        className="fill-[#426153]"
        x="262.56"
        y="289.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1659"
        className="fill-[#426153]"
        x="262.56"
        y="283.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1660"
        className="fill-[#426153]"
        x="220.81"
        y="271.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1661"
        className="fill-[#426153]"
        x="262.56"
        y="277.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1662" className="fill-[#426153]" x="322.24" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1663" className="fill-[#426153]" x="310.31" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1664" className="fill-[#426153]" x="310.31" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1665" className="fill-[#426153]" x="316.27" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1666" className="fill-[#426153]" x="322.24" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1667" className="fill-[#426153]" x="328.2" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1668" className="fill-[#426153]" x="334.17" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1669" className="fill-[#426153]" x="280.49" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1670" className="fill-[#426153]" x="334.17" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1671"
        className="fill-[#426153]"
        x="107.42"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1672"
        className="fill-[#426153]"
        x="155.14"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1673"
        className="fill-[#426153]"
        x="143.21"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1674"
        className="fill-[#426153]"
        x="137.25"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1675" className="fill-[#426153]" x="149.17" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1676"
        className="fill-[#426153]"
        x="155.14"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1677" className="fill-[#426153]" x="77.57" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1678" className="fill-[#426153]" x="59.68" y="150.39" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1679" className="fill-[#426153]" x="59.68" y="156.22" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1680"
        className="fill-[#426153]"
        x={activeIndex === 1 ? '102.46' : '101.46'}
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1681" className="fill-[#426153]" x="77.57" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1682" className="fill-[#426153]" x="83.53" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1683" className="fill-[#426153]" x="89.5" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1684"
        className="fill-[#426153]"
        x="387.86"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1685"
        className="fill-[#426153]"
        x="435.63"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1686" className="fill-[#426153]" x="369.99" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1687" className="fill-[#426153]" x="334.17" y="23.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1688" className="fill-[#426153]" x="298.38" y="34.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1689" className="fill-[#426153]" x="286.45" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1690" className="fill-[#426153]" x="208.85" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1691"
        className="fill-[#426153]"
        x="143.31"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1692" className="fill-[#426153]" x="208.85" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1693"
        className="fill-[#426153]"
        x="220.81"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1694"
        className="fill-[#426153]"
        x="173.07"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1695"
        className="fill-[#426153]"
        x="196.92"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1696"
        className="fill-[#426153]"
        x="161.14"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1697" className="fill-[#426153]" x="95.46" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1698" className="fill-[#426153]" x="47.75" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1699" className="fill-[#426153]" x="11.93" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1700"
        className="fill-[#426153]"
        x="113.39"
        y="266.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1701" className="fill-[#426153]" x="47.83" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1702" className="fill-[#426153]" x="95.46" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1703"
        className="fill-[#426153]"
        x="167.05"
        y="271.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1704"
        className="fill-[#426153]"
        x="173.07"
        y="323.95"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1705"
        className="fill-[#426153]"
        x="190.96"
        y="271.86"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1706"
        className="fill-[#426153]"
        x="262.56"
        y="295.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1707"
        className="fill-[#426153]"
        x="238.71"
        y="318.17"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1708"
        className="fill-[#426153]"
        x="274.49"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1709"
        className="fill-[#426153]"
        x="322.24"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1710"
        className="fill-[#426153]"
        x="101.46"
        y="167.78"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1711" className="fill-[#426153]" x="41.85" y="167.78" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1712"
        className="fill-[#426153]"
        x="399.84"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1713"
        className="fill-[#426153]"
        x="417.74"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1714"
        className="fill-[#426153]"
        x="417.74"
        y="271.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1715" className="fill-[#426153]" x="358.06" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1716" className="fill-[#426153]" x="352.09" y="11.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1717" className="fill-[#426153]" x="340.17" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1718" className="fill-[#426153]" x="381.92" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1719" className="fill-[#426153]" x="375.95" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1720" className="fill-[#426153]" x="340.17" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1721"
        className="fill-[#426153]"
        x="340.17"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1722"
        className="fill-[#426153]"
        x="375.95"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1723"
        className="fill-[#426153]"
        x="352.09"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1724"
        className="fill-[#426153]"
        x="387.88"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1725"
        className="fill-[#426153]"
        x="364.02"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1726"
        className="fill-[#426153]"
        x="340.17"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1727" className="fill-[#426153]" x="375.95" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1728"
        className="fill-[#426153]"
        x="364.02"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1729"
        className="fill-[#426153]"
        x="346.13"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1730"
        className="fill-[#426153]"
        x="352.09"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1731" className="fill-[#426153]" x="358.06" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1732"
        className="fill-[#426153]"
        x="381.92"
        y="266.09"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1733"
        className="fill-[#426153]"
        x="346.13"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1734"
        className="fill-[#426153]"
        x="375.95"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1735"
        className="fill-[#426153]"
        x="358.06"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1736"
        className="fill-[#426153]"
        x="358.06"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1737"
        className="fill-[#426153]"
        x="387.88"
        y="271.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1738"
        className="fill-[#426153]"
        x="334.17"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1739"
        className="fill-[#426153]"
        x="310.31"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1740"
        className="fill-[#426153]"
        x="286.45"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1741"
        className="fill-[#426153]"
        x="322.24"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1742"
        className="fill-[#426153]"
        x="298.38"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1743"
        className="fill-[#426153]"
        x="334.17"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1744"
        className="fill-[#426153]"
        x="310.31"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1745"
        className="fill-[#426153]"
        x="286.45"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1746" className="fill-[#426153]" x="322.24" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1747"
        className="fill-[#426153]"
        x="298.38"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1748"
        className="fill-[#426153]"
        x="334.17"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1749" className="fill-[#426153]" x="310.31" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1750"
        className="fill-[#426153]"
        x="334.17"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1751"
        className="fill-[#426153]"
        x="286.45"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1752"
        className="fill-[#426153]"
        x="322.24"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1753" className="fill-[#426153]" x="256.6" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1754" className="fill-[#426153]" x="262.56" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1755" className="fill-[#426153]" x="220.81" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1756" className="fill-[#426153]" x="274.49" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1757" className="fill-[#426153]" x="250.63" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1758"
        className="fill-[#426153]"
        x="244.67"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1759" className="fill-[#426153]" x="256.6" y="133.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1760"
        className="fill-[#426153]"
        x="232.74"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1761"
        className="fill-[#426153]"
        x="274.49"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1762"
        className="fill-[#426153]"
        x="250.63"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1763"
        className="fill-[#426153]"
        x={activeIndex === 3 ? '227.78' : '226.78'}
        y={activeIndex === 3 ? '195.68' : '196.68'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1764" className="fill-[#426153]" x="262.56" y="190.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1765"
        className="fill-[#426153]"
        x="238.71"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1766"
        className="fill-[#426153]"
        x="274.49"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1767" className="fill-[#426153]" x="262.56" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1768"
        className="fill-[#426153]"
        x="232.74"
        y="266.09"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1769"
        className="fill-[#426153]"
        x="226.78"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1770"
        className="fill-[#426153]"
        x="268.53"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1771"
        className="fill-[#426153]"
        x="250.63"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1772"
        className="fill-[#426153]"
        x="226.78"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1773"
        className="fill-[#426153]"
        x="262.56"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1774" className="fill-[#426153]" x="184.99" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1775" className="fill-[#426153]" x="214.81" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1776" className="fill-[#426153]" x="214.81" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1777"
        className="fill-[#426153]"
        x="190.96"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1778"
        className="fill-[#426153]"
        x="190.96"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1779"
        className="fill-[#426153]"
        x="161.14"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1780"
        className="fill-[#426153]"
        x="173.07"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1781"
        className="fill-[#426153]"
        x="208.85"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1782"
        className="fill-[#426153]"
        x="184.99"
        y={activeIndex === 0 ? '189.9' : '190.9'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1783"
        className="fill-[#426153]"
        x="161.14"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1784"
        className="fill-[#426153]"
        x="208.85"
        y="266.09"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1785" className="fill-[#426153]" x="167.1" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1786"
        className="fill-[#426153]"
        x="202.89"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1787"
        className="fill-[#426153]"
        x="179.03"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1788"
        className="fill-[#426153]"
        x="214.81"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1789"
        className="fill-[#426153]"
        x="190.96"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1790" className="fill-[#426153]" x="167.1" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1791"
        className="fill-[#426153]"
        x="149.17"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1792" className="fill-[#426153]" x="41.79" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1793" className="fill-[#426153]" x="83.53" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1794" className="fill-[#426153]" x="83.53" y="248.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1795" className="fill-[#426153]" x="83.53" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1796" className="fill-[#426153]" x="53.71" y="295.05" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1797" className="fill-[#426153]" x="71.6" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1798" className="fill-[#426153]" x="11.93" y="260.31" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1799" className="fill-[#426153]" x="23.86" y="300.83" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1800" className="fill-[#426153]" x="23.86" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1801"
        className="fill-[#426153]"
        x="155.14"
        y="266.09"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1802"
        className="fill-[#426153]"
        x="101.46"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1803"
        className="fill-[#426153]"
        x="137.25"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1804"
        className="fill-[#426153]"
        x="113.39"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1805"
        className="fill-[#426153]"
        x="149.17"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1806"
        className="fill-[#426153]"
        x="125.32"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1807"
        className="fill-[#426153]"
        x="238.71"
        y="289.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1808"
        className="fill-[#426153]"
        x="238.71"
        y="277.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1809" className="fill-[#426153]" x="298.38" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1810" className="fill-[#426153]" x="334.17" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1811" className="fill-[#426153]" x="310.31" y="57.85" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1812" className="fill-[#426153]" x="328.2" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1813"
        className="fill-[#426153]"
        x="131.28"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1814"
        className="fill-[#426153]"
        x="113.39"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1815"
        className="fill-[#426153]"
        x="143.21"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1816" className="fill-[#426153]" x="71.6" y="40.51" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1817"
        className="fill-[#426153]"
        x="131.28"
        y="289.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1818" className="fill-[#426153]" x="83.53" y="173.56" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1819"
        className="fill-[#426153]"
        x="387.88"
        y="277.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1820"
        className="fill-[#426153]"
        x="411.77"
        y="156.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1821" className="fill-[#426153]" x="310.31" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1822" className="fill-[#426153]" x="244.67" y="28.9" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1823"
        className="fill-[#426153]"
        x="173.07"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1824" className="fill-[#426153]" x="256.6" y="115.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1825"
        className="fill-[#426153]"
        x="184.99"
        y="156.22"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1826"
        className="fill-[#426153]"
        x={activeIndex === 2 ? '132.28' : '131.28'}
        y={activeIndex === 2 ? '178.34' : '179.34'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1827" className="fill-[#426153]" x="11.93" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1828"
        className="fill-[#426153]"
        x="137.25"
        y="277.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1829" className="fill-[#426153]" x="83.59" y="115.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1830"
        className="fill-[#426153]"
        x="161.11"
        y="318.17"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1831"
        className="fill-[#426153]"
        x="238.71"
        y="300.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1832"
        className="fill-[#426153]"
        x="304.35"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1833" className="fill-[#426153]" x="65.67" y="185.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1834"
        className="fill-[#426153]"
        x="405.81"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1835"
        className="fill-[#426153]"
        x="405.81"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1836" className="fill-[#426153]" x="352.09" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1837" className="fill-[#426153]" x="375.95" y="75.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1838" className="fill-[#426153]" x="352.09" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1839"
        className="fill-[#426153]"
        x="364.02"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1840"
        className="fill-[#426153]"
        x="375.95"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1841"
        className="fill-[#426153]"
        x="387.88"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1842"
        className="fill-[#426153]"
        x="369.99"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1843"
        className="fill-[#426153]"
        x="358.06"
        y="167.78"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1844"
        className="fill-[#426153]"
        x="393.84"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1845"
        className="fill-[#426153]"
        x="364.02"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1846"
        className="fill-[#426153]"
        x="358.06"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1847"
        className="fill-[#426153]"
        x="322.24"
        y="144.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1848"
        className="fill-[#426153]"
        x="334.17"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1849"
        className="fill-[#426153]"
        x="286.45"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1850"
        className="fill-[#426153]"
        x="298.38"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1851"
        className="fill-[#426153]"
        x="310.31"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1852"
        className="fill-[#426153]"
        x="322.24"
        y="167.78"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1853"
        className="fill-[#426153]"
        x="280.49"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1854" className="fill-[#426153]" x="256.6" y="92.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1855" className="fill-[#426153]" x="220.81" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1856" className="fill-[#426153]" x="262.56" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1857"
        className="fill-[#426153]"
        x="250.63"
        y="138.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1858"
        className="fill-[#426153]"
        x="226.78"
        y="104.15"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1859"
        className="fill-[#426153]"
        x="238.71"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1860"
        className="fill-[#426153]"
        x="250.63"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1861"
        className="fill-[#426153]"
        x="268.53"
        y="167.78"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1862"
        className="fill-[#426153]"
        x="226.78"
        y="260.31"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1863"
        className="fill-[#426153]"
        x="262.56"
        y="242.97"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1864"
        className="fill-[#426153]"
        x="274.49"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1865" className="fill-[#426153]" x="196.92" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1866" className="fill-[#426153]" x="208.85" y="46.29" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1867"
        className="fill-[#426153]"
        x="196.92"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1868"
        className="fill-[#426153]"
        x="161.14"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1869"
        className="fill-[#426153]"
        x="173.07"
        y="185.12"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1870"
        className="fill-[#426153]"
        x="179.03"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1871"
        className="fill-[#426153]"
        x="190.96"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1872"
        className="fill-[#426153]"
        x="202.89"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1873"
        className="fill-[#426153]"
        x="161.14"
        y="271.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1874" className="fill-[#426153]" x="89.5" y="266.09" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1875" className="fill-[#426153]" x="83.53" y="237.19" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1876" className="fill-[#426153]" x="53.71" y="283.49" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1877" className="fill-[#426153]" x="35.78" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1878" className="fill-[#426153]" x="35.78" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1879"
        className="fill-[#426153]"
        x="149.17"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1880"
        className="fill-[#426153]"
        x="101.46"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1881"
        className="fill-[#426153]"
        x="226.78"
        y="306.61"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1882"
        className="fill-[#426153]"
        x="244.67"
        y="271.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1883" className="fill-[#426153]" x="334.17" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1884" className="fill-[#426153]" x="316.27" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1885"
        className="fill-[#426153]"
        x="119.35"
        y="202.46"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1886" className="fill-[#426153]" x="65.64" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1887"
        className="fill-[#426153]"
        x={activeIndex === 1 ? '88.5' : '89.5'}
        y={activeIndex === 1 ? '186.12' : '185.12'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1888" className="fill-[#426153]" x="387.88" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1889" className="fill-[#426153]" x="280.49" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1890" className="fill-[#426153]" x="232.74" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1891"
        className="fill-[#426153]"
        x="155.14"
        y="150.42"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1892" className="fill-[#426153]" x="77.57" y="277.71" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1893"
        className="fill-[#426153]"
        x="179.03"
        y="300.83"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1894"
        className="fill-[#426153]"
        x="226.78"
        y="329.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1895" className="fill-[#426153]" x="59.68" y="144.61" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1896"
        className="fill-[#426153]"
        x="411.77"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1897" className="fill-[#426153]" x="387.88" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1898"
        className="fill-[#426153]"
        x="387.88"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1899"
        className="fill-[#426153]"
        x="352.09"
        y="109.93"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1900"
        className="fill-[#426153]"
        x="358.06"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1901"
        className="fill-[#426153]"
        x="393.84"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1902"
        className="fill-[#426153]"
        x="286.45"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1903"
        className="fill-[#426153]"
        x="310.31"
        y="115.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1904"
        className="fill-[#426153]"
        x="334.17"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1905"
        className="fill-[#426153]"
        x="298.38"
        y="156.22"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1906" className="fill-[#426153]" x="256.6" y="80.97" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1907"
        className="fill-[#426153]"
        x="238.71"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1908" className="fill-[#426153]" x="262.56" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1909"
        className="fill-[#426153]"
        x="226.78"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1910"
        className="fill-[#426153]"
        x="220.81"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1911"
        className="fill-[#426153]"
        x="250.63"
        y="214.07"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1912"
        className="fill-[#426153]"
        x="179.03"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1913"
        className="fill-[#426153]"
        x="196.92"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1914"
        className="fill-[#426153]"
        x="214.81"
        y="248.75"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1915"
        className="fill-[#426153]"
        x="184.99"
        y="295.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1916" className="fill-[#426153]" x="71.6" y="254.53" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1917" className="fill-[#426153]" x="77.57" y="271.93" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1918"
        className="fill-[#426153]"
        x="113.39"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1919"
        className="fill-[#426153]"
        x="137.25"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1920" className="fill-[#426153]" x="286.45" y="86.75" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1921" className="fill-[#426153]" x="238.71" y="34.68" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1922" className="fill-[#426153]" x="53.71" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1923" className="fill-[#426153]" x="346.13" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1924"
        className="fill-[#426153]"
        x="220.81"
        y="150.39"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1925"
        className="fill-[#426153]"
        x="101.41"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1926"
        className="fill-[#426153]"
        x="352.09"
        y="254.53"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1927" className="fill-[#426153]" x="346.13" y="23.12" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1928"
        className="fill-[#426153]"
        x="340.17"
        y="127.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1929"
        className="fill-[#426153]"
        x="358.06"
        y="156.22"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1930"
        className="fill-[#426153]"
        x="298.38"
        y="133.05"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1931"
        className="fill-[#426153]"
        x="286.45"
        y="173.56"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1932" className="fill-[#426153]" x="226.78" y="52.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1933"
        className="fill-[#426153]"
        x="274.49"
        y="196.68"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1934"
        className="fill-[#426153]"
        x="238.71"
        y="231.41"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1935" className="fill-[#426153]" x="167.1" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1936" className="fill-[#426153]" x="167.1" y="225.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1937" className="fill-[#426153]" x="83.53" y="214.07" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1938"
        className="fill-[#426153]"
        x="125.32"
        y="237.19"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1939" className="fill-[#426153]" x="304.35" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1940" className="fill-[#426153]" x="95.46" y="162" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1941" className="fill-[#426153]" x="59.68" y="202.46" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1942" className="fill-[#426153]" x="375.95" y="63.63" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1943"
        className="fill-[#426153]"
        x="358.06"
        y="225.63"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1944"
        className="fill-[#426153]"
        x="334.17"
        y="219.85"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1945"
        className="fill-[#426153]"
        x="244.67"
        y="156.22"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1946"
        className="fill-[#426153]"
        x="214.81"
        y="179.34"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1947" className="fill-[#426153]" x="11.93" y="289.27" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1948"
        className="fill-[#426153]"
        x="125.32"
        y={activeIndex === 2 ? '191.9' : '190.9'}
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1949"
        className="fill-[#426153]"
        x="208.85"
        y="289.27"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1950"
        className="fill-[#426153]"
        x="369.99"
        y="208.24"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1951"
        className="fill-[#426153]"
        x="268.53"
        y="121.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect
        id="rect-1952"
        className="fill-[#426153]"
        x="155.14"
        y="277.71"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1953" className="fill-[#426153]" x="184.99" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect id="rect-1954" className="fill-[#426153]" x="322.24" y="98.37" width="2.95" height="2.95" rx=".5" ry=".5" />
      <rect
        id="rect-1955"
        className="fill-[#426153]"
        x="238.71"
        y="283.49"
        width="2.95"
        height="2.95"
        rx=".5"
        ry=".5"
      />
      <rect id="rect-1956" className="fill-[#426153]" x="196.92" y="69.41" width="2.95" height="2.95" rx=".5" ry=".5" />

      {/* ST1 Elements Group - placed at end for proper z-index/stacking */}
      <g>
        <rect
          id="st1-element-0"
          className={cn('z-0 fill-[#b9ff3f] stroke-green-neon transition-all duration-300', {
            'stroke-[3.5px]': activeIndex === 3,
            'stroke-0': activeIndex !== 3,
          })}
          x="220.81"
          y="202.46"
          width="2.95"
          height="2.95"
          rx=".5"
          ry=".5"
          onClick={() => setActiveIndex(3)}
        />

        <rect
          id="st1-element-1"
          className={cn('z-0 fill-[#b9ff3f] stroke-green-neon transition-all duration-300', {
            'stroke-[3.5px]': activeIndex === 1,
            'stroke-0': activeIndex !== 1,
          })}
          x="95.46"
          y="179.34"
          width="2.95"
          height="2.95"
          rx=".5"
          ry=".5"
          onClick={() => setActiveIndex(1)}
        />

        <rect
          id="st1-element-2"
          className={cn('z-0 fill-[#b9ff3f] stroke-green-neon transition-all duration-300', {
            'stroke-[3.5px]': activeIndex === 2,
            'stroke-0': activeIndex !== 2,
          })}
          x="125.32"
          y="185.12"
          width="2.95"
          height="2.95"
          rx=".5"
          ry=".5"
          onClick={() => setActiveIndex(2)}
        />

        <rect
          id="st1-element-3"
          className={cn('z-0 fill-[#b9ff3f] stroke-green-neon transition-all duration-300', {
            'stroke-[3.5px]': activeIndex === 0,
            'stroke-0': activeIndex !== 0,
          })}
          x="184.99"
          y="196.68"
          width="2.95"
          height="2.95"
          rx=".5"
          ry=".5"
          onClick={() => setActiveIndex(0)}
        />

        <rect
          id="st1-element-4"
          className={cn('z-0 fill-[#b9ff3f] stroke-green-neon transition-all duration-300', {
            'stroke-[3.5px]': activeIndex === 4,
            'stroke-0': activeIndex !== 4,
          })}
          x="244.67"
          y="127.27"
          width="2.95"
          height="2.95"
          rx=".5"
          ry=".5"
          onClick={() => setActiveIndex(4)}
        />
      </g>

      {/* Text indicators using native SVG text elements */}
      {elementPositions.map((position, index) => (
        <g key={index}>
          <AnimatePresence mode="wait">
            {activeIndex === index && elements[index]?.button_text && (
              <motion.g
                key={`text-${index}`}
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
                transition={{
                  duration: 0.5,
                  ease: 'easeInOut',
                  exit: {duration: 0.4, ease: 'easeOut'},
                  enter: {duration: 0.4, ease: 'easeIn', delay: 0.1},
                }}
              >
                {/* Background rectangle for the text */}
                <rect
                  x={position.x + 9 + (activeIndex === 4 || (activeIndex === 3 && isMobile) ? -68 : 0)}
                  y={position.y - 26 + (activeIndex === 3 && isMobile ? 34 : 0)}
                  width={getTextWidth(elements[activeIndex].button_text!)}
                  height="21.5"
                  rx="2"
                  ry="2"
                  fill="#B9FF3F"
                  stroke="rgba(0,0,0,0.1)"
                  strokeWidth="0.5"
                />
                {/* Text element */}
                <text
                  x={position.x + 17 + (activeIndex === 4 || (activeIndex === 3 && isMobile) ? -68 : 0)}
                  y={position.y - 26 + 13 + (activeIndex === 3 && isMobile ? 34 : 0)}
                  fontSize="7"
                  fontWeight="600"
                  style={{textTransform: 'uppercase', letterSpacing: '0.55px'}}
                  fill="#000000"
                >
                  {elements[activeIndex].button_text}
                </text>
              </motion.g>
            )}
          </AnimatePresence>
        </g>
      ))}
    </svg>
  )
}
